// The vault engine: everything the app does against the chain, in one
// place, so screens stay declarative (docs/08-app-design.md 5.1 step 6, 5.2,
// 5.7, 5.8 and 7). It composes the SDK exactly the way the mainnet canary
// did (sdk/ts/scripts/mainnet-canary.ts, landed 2026-09-24): the vault's fee
// reserve pays, `VaultSigner` reserves and signs through the worker, the
// ledger lives in IndexedDB, and every send is simulate-then-send-then-sync.
// No secret is held here: the master, the trees and the fee key all live in
// the worker. The fee reserve is the vault's own derived key (n = 0 of the
// fee branch, docs/18-one-fee-reserve.md 2.4), re-derived in the worker at
// every unlock; `relayer` is a worker-backed signer whose `sign` is the
// worker's `feeSign`, so no seed is ever on the main thread.
//
// Every signed action goes through one pipeline (G5 R41-R46):
//   guard      - the RPC's network is checked against the deployment this
//                build was made for (docs/18 2.5), then the deployed
//                program's hash is read and compared, before every
//                action, both never cached (R42); a key that needs a
//                refresh refuses the action before any key is reserved
//                (R44); the gas tank must cover its own account minimum F,
//                the fee, any rent the transaction creates and one refresh
//                in reserve, read live (R49; R78, docs/18 5.1 A)
//   sign       - the header the signer returns is the header that is sent,
//                automatic successor included (R43)
//   simulate   - a refusal here is "rejected": nothing was sent, the key
//                is retired
//   send       - the signature is known from the wire and recorded as
//                sent before the send, so a lost reply leaves an
//                "unknown" record, never a phantom (R45)
//   confirm    - "failed on chain" retires the key; a timeout is "unknown"
//                and stays unknown until `reconcile` resolves it
//   settle     - reads after a confirmed landing can only add a note; they
//                never turn a landed action into a failed one (R45)
// An error of the transaction itself (the fee reserve could not pay, an
// account would be left below its minimum) is never a route failure: no
// BOUNDED re-quote, no resend, no venue exclusion (R78, docs/18 5.1 B).
// Activate writes the tree-0 marker only from this device's own Initialize,
// finalized and successful, and a matching finalized readback (R79, 5.1 D).
//
// Older reserves (docs/18 section 6): on a legacy device, the per-device
// relayer key of earlier releases may still hold SOL. Unlock never opens its
// envelope; `olderReserve` reads its balance by its recorded address, and
// `openOlderReserve` opens the envelope on demand into the worker's
// sweep-only set (recording a pre-R48 device's address through the session's
// compare-and-set writer). `sweep` moves it into the derived reserve as one
// worker-compiled System transfer (Form A: the older key pays; Form B: the
// derived reserve pays and the older key co-signs), sent by `sendSignedV1`
// with `assertOpen` as its checkpoint, so nothing is sent after a Lock. The
// network check, both balances, F and the guard (an unresolved record of the
// current tree other than a recovery record blocks) are read again after the
// last wait and must reproduce the reviewed figures; only empty System
// accounts are priced. An `InsufficientGas` refusal points to a movable
// older reserve. One sweep at a time, across lock and unlock: the session
// keeps a sent sweep whose outcome is not known yet (`pendingSweep`), and
// until it is known a second refuses and the reading waits. An error before
// the first send attempt is `SweepNotSent`; after the send the outcome also
// goes to the session (`onSweepOutcome`), which keeps it as its notice unless
// the page that pressed Move is still there to show it.

import { address } from '@solana/kit';
import {
  type AccountMetaSpec,
  type Authorization,
  EARLIER_READ_CAP_MS,
  EarlierReads,
  type ExecHeaderSpec,
  type Expected,
  type FeeSide,
  IndexedDbLedgerStore,
  type InnerIx,
  JupiterClient,
  LADDER_LIMITS,
  type LadderResult,
  type LeafRecord,
  MARKER_LIFETIME_SLOTS,
  NOT_APPLIED_NOTE_PREFIX,
  lstSharedAccounts,
  swapVenues,
  type Purpose,
  ROTATE_AT,
  type SealedEnvelope,
  type SendOutLimits,
  type SendOutRefusal,
  SignerError,
  type SwapParams,
  type SweepPlan,
  TAG_VAULT,
  type SwapReceipt,
  type TokenPrograms,
  type TreeMarker,
  UNRESOLVED,
  VAULT_LEN,
  VaultSigner,
  type VaultSnapshot,
  WSOL_MINT,
  type WorkerBackend,
  type WorkerClient,
  actionCount,
  activationLimitsFrom,
  activationRequirement,
  addressBytes,
  assembleExecute,
  MintUnpriceable,
  associatedTokenAccountLen,
  ataAddress,
  concat,
  createAtaIdempotent,
  drawActivationR,
  equalBytes,
  exportPending,
  feeAccount,
  feeMint,
  feeOf,
  feeSide,
  headerBytes,
  hex,
  initializeInstruction,
  jupiterMinOut,
  maxInForRoute,
  ordinaryRequirement,
  plainActionRequirement,
  protocolFee,
  quoteWithinLimits,
  receiptAccounts,
  refreshRequirement,
  sendOutCheck,
  sendOutLimits,
  snapshot,
  suggestedTopUp,
  swapReceipt,
  sweepPlan,
  transactionInstructionsAround,
  u64le,
  u8,
  vaultAddress,
  vaultId,
} from '@thevault/sdk';
import { TOKEN_PROGRAM, wrapAux, wrapHeader, wrapTransactionInstructions } from '@thevault/sdk/canary';
import { sendSignedV1 } from '@thevault/sdk/relayer';
import { type Instruction, type Limits, PLAIN_LIMITS, compileV1 } from '@thevault/sdk/v1';
import * as sk from '@thevault/sdk/stake';
import { FLAGS, U64_MAX, u32le } from '@thevault/sdk/digest';
import { assertDeployment as checkDeployment } from './deployment';
import { deployedProgramHash } from './program';
import { type PositionView, type StakingGate, type VoteCheck, STAKE_PROGRAM_B58, SYSVAR, checkVoteAccount, noteCommission, parseClock, parseRent, rentFor, stakingGate, viewPosition } from './staking';
import { type OnChainAccount, type RelayerSigner, RelayerError, Rpc, type SendResult, type SignatureStatus, type TransactionError, isFinalizedSuccess, isRouteFailureError, signV1, toBase58, transactionError } from './rpc';
import type { AppSettings } from './storage';
import { symbolOf } from './tokens';

/**
 * Limits for the plain transactions, the ones that touch only the System
 * program and the vault program: Activate, the key refresh, the native
 * withdrawal and the gas tank's send-out (same figures as the canary). The
 * constant is the SDK's (sdk/ts/src/v1.ts), re-exported here under the same
 * name, so the worker's sweeps and fee-key checks and this engine's
 * transactions share one definition (docs/18 2.4: fee-key messages are
 * plain). The loaded-data limit is the protocol maximum, 64 MiB
 * (`PLAIN_LOADED_ACCOUNTS_DATA_SIZE`, sdk/ts/src/v1.ts; R70), a selected
 * conservative policy (R73): the cluster
 * counts the vault's whole ProgramData, which anyone may extend up to the
 * 10 MiB account cap while the program is upgradeable, and a withdrawal's
 * recipient can be any address, even a 10 MiB LoaderV3 Program account with
 * a 10 MiB ProgramData, so a figure sized to the accounts as they are does
 * not hold once the key has signed. With the app's own generated payer,
 * which holds no data, each plain shape needs at most 31,457,828 B by the
 * bounds of its keys' roles (sdk/ts/src/loaded-data.ts `PLAIN_SHAPES`), which
 * 32 MiB would also cover; the maximum leaves margin, and no plain shape
 * needs a check before its key is reserved. That figure assumes this payer;
 * a caller-supplied payer may be an 80-byte nonce account
 * (`PLAIN_EXTERNAL_PAYER_WORST_CASE`, 31,457,908 B). The figure is not part
 * of the fee (base fee plus `priorityFeeLamports`). Covered end to end by
 * e2e/loaded-data.spec.ts.
 */
export { PLAIN_LIMITS };
/**
 * Limits for a transaction that also invokes the SPL Token or ATA program by
 * CPI (the wrap: createAtaIdempotent + Execute + SyncNative; the unwrap; a
 * token withdrawal). Same figures as the mainnet canary's `TOKEN_TOUCHING_LIMITS`
 * (sdk/ts/scripts/mainnet-canary.ts): the ladder's ceiling (`LADDER_LIMITS`),
 * the protocol maximum, which the plain limit now shares. The wrap and the
 * unwrap fit under it by their keys' roles. A token withdrawal's bound is
 * loose (`TOKEN_SHAPES`): it fits whenever the System program's account and
 * the two token accounts hold at most 14,574,000 B of data together, which
 * nothing here proves, so only its simulation, after the key has signed,
 * shows that it fits.
 */
export const TOKEN_TOUCHING_LIMITS: Limits = { computeUnitLimit: 200_000, loadedAccountsDataSizeLimit: LADDER_LIMITS.loadedAccountsDataSizeLimit, priorityFeeLamports: 10_000n };

/**
 * The fee of one plain action: one signature's base fee plus the priority fee above (15,000 lamports). It is also K,
 * the one plain action every ordinary requirement holds back for a key refresh, and the step of "about N actions"
 * (sdk/ts/src/fee-reserve.ts; docs/18 5.1 A). Activation's own figure comes from `activationCost()`, read live; there
 * is no fixed activation constant any more.
 */
export const LAMPORTS_PER_ACTION = feeOf(PLAIN_LIMITS);

/**
 * The limits of one Initialize carrier (docs/18 5.1 D step 7): `PLAIN_LIMITS` with 200,000 + r compute units, r drawn
 * per attempt in [0, 65,536). The fee does not change (the priority fee is its own absolute amount), so two devices'
 * carriers built on one blockhash differ unless their draws are equal (probability 2^-16 per pair): distinguished
 * probabilistically, not unique.
 */
export function activationLimits(r: number): Limits {
  return activationLimitsFrom(PLAIN_LIMITS, r);
}

/** How long Activate waits for its own Initialize to be finalized before it reports the outcome as unknown; Check again can still find it afterwards, while the engine holds the attempt. */
export const ACTIVATION_FINALITY_MS = 90_000;
/** The System program id (32 zero bytes), for a plain lamport transfer out of the gas tank. */
const SYSTEM_PROGRAM_ID = new Uint8Array(32);

/** System program Transfer: u32le(2) || u64le(lamports) (mirrors sdk/ts/scripts/mainnet-canary.ts's systemTransfer helper). */
function systemTransferInstruction(from: Uint8Array, to: Uint8Array, lamports: bigint): Instruction {
  const data = new Uint8Array(12);
  new DataView(data.buffer).setUint32(0, 2, true);
  new DataView(data.buffer).setBigUint64(4, lamports, true);
  return { programId: SYSTEM_PROGRAM_ID, accounts: [{ pubkey: from, isSigner: true, isWritable: true }, { pubkey: to, isSigner: false, isWritable: true }], data };
}

/**
 * The side of a swap between two base58 mints that pays the treasury fee,
 * by the program's own rule (program/src/fee.rs `fee_side`, through the
 * SDK's `feeSide`): a USDC or USDT side (the output when both are), else a
 * SOL side, else the output. For the swap form, which must leave room for a
 * fee paid from the input; a mint that is not a valid address yet counts as
 * the output side (the form cannot quote it anyway).
 */
export function feeSideOfMints(inMintB58: string, outMintB58: string): FeeSide {
  try {
    return feeSide(addressBytes(address(inMintB58)), addressBytes(address(outMintB58)));
  } catch {
    return 'output';
  }
}

/** Public facts about this device's vault, stored unsealed in IndexedDB (app/src/storage.ts) so unlock never has to derive tree 0 to know where the vault is. */
export interface VaultInfo {
  /** base58 */
  address: string;
  /** hex, 32 bytes */
  vaultId: string;
  /** hex, 41 bytes: root || P of tree 0 */
  genesisKey: string;
  bump: number;
  /**
   * base58, the vault's fee reserve: the derived n = 0 address of the fee branch (docs/18 2.4, section 3). Written at
   * Create and Restore, and at the first unlock under this release on a device set up before it. Display data only:
   * it lets a locked device (the Forget sheet, the entrance) read the reserve's balance. While unlocked, every address
   * shown, every QR code and every transaction comes from the key the worker derived (`Engine.relayerAddress`), and
   * unlock rewrites a stored value that differs from it. No generation is stored beside it; a `feeGeneration` found
   * in the row (another build's, or tampered) is ignored and never copied.
   */
  feeWallet?: string;
  /**
   * Legacy: base58, the per-device relayer key of an earlier release (its envelope is `sealedRelayer` in storage.ts),
   * public so a locked device can still say what "Forget this device" would leave behind (R48). Never written for a
   * setup made under this release. Absent on legacy devices set up before it was stored (pre-R48), until the
   * envelope is first opened (docs/18 section 6).
   */
  relayer?: string;
}

export function hexOf(b: Uint8Array): string {
  return Array.from(b, (x) => x.toString(16).padStart(2, '0')).join('');
}
export function bytesOf(hex: string): Uint8Array {
  return Uint8Array.from(hex.match(/../g) ?? [], (x) => parseInt(x, 16));
}

/** Tree 0's verifying key names the vault (docs/01 section 7.1); derived in the worker, only the public key crosses. */
export async function vaultInfoFromGenesis(programIdB58: string, genesisKey: Uint8Array): Promise<VaultInfo> {
  const va = await vaultAddress(address(programIdB58), genesisKey);
  return { address: va.address, vaultId: hexOf(vaultId(genesisKey)), genesisKey: hexOf(genesisKey), bump: va.bump };
}

/**
 * What sits at the vault's address. `prefunded` is a deposit that arrived
 * before activation: an empty, System-owned account holding lamports, which
 * Initialize adopts in place (program/src/instructions/initialize.rs, the
 * Allocate/Assign path), so the deposit is never stranded (R40). `foreign`
 * is anything else that is not this program's vault.
 */
export type ChainState =
  | { kind: 'absent' }
  | { kind: 'prefunded'; account: OnChainAccount }
  | { kind: 'vault'; account: OnChainAccount; snap: VaultSnapshot }
  | { kind: 'tombstone'; account: OnChainAccount; snap: VaultSnapshot }
  | { kind: 'foreign'; account: OnChainAccount };

export interface EngineDeps {
  rpc: Rpc;
  store: IndexedDbLedgerStore;
  backend: WorkerBackend;
  relayer: RelayerSigner;
  programId: Uint8Array;
  info: VaultInfo;
  /** The live settings (Jupiter endpoint, venue exclusions), read at each action. */
  settings: () => AppSettings;
  /** The Jupiter API key opened at unlock, or null. */
  jupiterApiKey: () => string | null;
  /** Section 7: whether this session skips the program-hash check. Held in memory only and reset at lock (R42). */
  development: () => boolean;
  /** The session's worker client: the older reserve's envelope is opened into its sweep-only set and the sweep is compiled and signed there (docs/18 2.4, section 6). */
  client: WorkerClient;
  /** The legacy relayer envelope this device stores (storage.ts `loadSealedRelayer`), or undefined on a device set up under this release. Read on demand, never at unlock (docs/18 section 3); a failed read is `EnvelopeUnreadable`. */
  legacyEnvelope: () => Promise<SealedEnvelope | undefined>;
  /**
   * Records a pre-R48 legacy device's older address (docs/18 section 6) through the compare-and-set writer with the
   * setup identity the session captured at unlock (storage.ts `updateFeeFields`, R84): resolves once it is written or
   * already stored with this value, and rejects (`SetupChanged`) otherwise.
   */
  recordLegacyAddress: (address: string) => Promise<void>;
  /**
   * A sent sweep's outcome (docs/18 section 6, "A lock after the send lets the confirmation finish and shows the
   * outcome"): called by `sweep` once `sendSignedV1` resolves or fails after the first send attempt, before it returns
   * or rethrows, whether or not the engine is still open, so the session can keep it for the door and Home when a
   * Lock came first or the page that pressed Move has gone. Never called for a sweep stopped before the send.
   */
  onSweepOutcome?: (outcome: SweepOutcome) => void;
  /**
   * The session's one-sweep latch (docs/18 section 6, one sweep at a time), read by every engine: the sweep sent and
   * not yet known to have landed, failed on chain or expired unlanded. It outlives this runtime, so the engine of the
   * next unlock neither moves the older key again nor offers a move while the first may still land.
   */
  pendingSweep: () => PendingSweep | null;
  /** Sets the latch at a sweep's first send attempt, and clears it (null) once that sweep's outcome is known. */
  setPendingSweep: (sweep: PendingSweep | null) => void;
}

/** A sent sweep with no known outcome yet (`EngineDeps.pendingSweep`): enough to watch it, `form` and `amount` the plan's. */
export interface PendingSweep {
  signature: string;
  lastValidBlockHeight: bigint;
  form: 'A' | 'B';
  amount: bigint;
}

/**
 * A sent sweep's outcome for `EngineDeps.onSweepOutcome`: `form` and `amount` are the plan's, `signature` the payer's
 * (the transaction's). `landed` is true, with `detail` null, once the transfer confirmed; otherwise `detail` says what
 * is known, in full sentences in the user's words. `failed` is true for a transfer that landed and failed on chain
 * (its payer, account 0, named by the form, paid the fee and nothing else moved); false with `landed` false, it was
 * not confirmed and may still land, which the older reserve's balance settles.
 */
export interface SweepOutcome {
  signature: string;
  amount: bigint;
  form: 'A' | 'B';
  landed: boolean;
  failed: boolean;
  detail: string | null;
}

/** The slot clock of docs/08 section 6: two or more `getSlot` observations give a measured slot rate for the countdown; 400 ms until then. */
export class SlotClock {
  private samples: { slot: number; at: number }[] = [];
  observe(slot: number, at = Date.now()): void {
    this.samples.push({ slot, at });
    if (this.samples.length > 8) this.samples.shift();
  }
  latest(): { slot: number; at: number } | null {
    return this.samples[this.samples.length - 1] ?? null;
  }
  msPerSlot(): number {
    const first = this.samples[0];
    const last = this.samples[this.samples.length - 1];
    if (!first || !last || last.at - first.at < 5_000 || last.slot <= first.slot) return 400;
    return Math.min(1_000, Math.max(200, (last.at - first.at) / (last.slot - first.slot)));
  }
}

/**
 * The steps of any signed action (wrap, withdraw, unwrap, swap) and its four
 * outcomes (docs/08 5.4, 5.5 step 3; R45): `landed`; `rejected` (refused in
 * the pre-flight check, never sent); `failed` (landed and failed on chain,
 * nothing moved); `unknown` (sent, not confirmed yet). Only `landed` and
 * `failed` are chain facts; `rejected` means nothing reached the chain, and
 * `unknown` means the app does not know. An unknown outcome resolves later
 * to `landed`, `failed`, or `expired` (its signature's validity passed with
 * no confirmation: the ledger retires the key, and nothing moved as far as
 * the app can see; R56).
 */
export type ActionStep = 'signing' | 'checking' | 'sending' | 'landed' | 'rejected' | 'failed' | 'unknown' | 'expired';
export interface ActionProgress {
  step: ActionStep;
  signature?: string;
  error?: string;
  leaf?: number;
  note?: string;
  validUntilSlot?: bigint;
}
/** The wrap ("Make swappable") reports the same steps and outcomes. */
export type WrapStep = ActionStep;
export type WrapProgress = ActionProgress;

export interface ActionResult {
  signature: string;
  leaf: number;
  transactionSlot: number;
  /** Set when the reads after the confirmed landing failed: the action landed all the same, and the ledger catches up at the next read. */
  readback: string | null;
}

/** The note "pending.json" carries (docs/08 5.9), qualified for R76 (docs/17 case h): no marker travels, but an imported landed key change can still earn one. */
export const EXPORT_NOTE = 'This export carries no device marker. A device that imports it refreshes the key before acting. It can skip that refresh only if a key change in this export has landed, the device opens the vault within about an hour of that landing, and no action has landed under the new key since.';

/** Said for an attempt refused in the pre-flight check: nothing was sent, the key is retired all the same (docs/08 2, 5.4). */
export const REJECTED_SENTENCE = 'That attempt was not sent: the network refused it in the pre-flight check, so nothing moved. The key it used is retired, and the vault refreshes its key before anything else.';
/** Said for an attempt that landed and failed on chain. */
export const FAILED_SENTENCE = 'That attempt landed and failed on chain, so nothing moved. The key it used is retired, and the vault refreshes its key before anything else.';
/** Said for an attempt whose outcome the app does not know: nothing is retired, nothing new is signed. */
export const UNKNOWN_SENTENCE = 'That attempt was sent, but the network has not confirmed it yet. Nothing new is signed until its outcome is known.';
/** Said for an attempt whose signature expired with no confirmation (R56): retired by the ledger, not a chain fact. */
export const EXPIRED_SENTENCE = 'That attempt expired before the network confirmed it, and nothing moved as far as the app can see. The key it used is retired, and the vault refreshes its key before anything else.';
/**
 * Said for a record the reconciliation step burned as not applied (R69, docs/17 row 6): tree t moved on through
 * another authorization of the same tree, proven from that authorization's own transaction, so this one cannot apply.
 */
export const NOT_APPLIED_SENTENCE = "That attempt did not take effect, so nothing moved: the vault's key moved on through another authorization.";
/** Said for a key the ledger retired for a reason the app cannot name more precisely. */
export const RETIRED_SENTENCE = "That attempt's key was retired without a confirmed outcome. The vault refreshes its key before anything else.";

export type FailureKind = 'rejected' | 'failed';

/**
 * The outcome a reconciled record stands for, from the reason the ledger
 * recorded when it retired the key (R56): only a confirmed on-chain error
 * is "failed"; a signature that expired unconfirmed is "expired"; a
 * pre-flight refusal is "rejected". A burn is the signer's conservative
 * decision, never by itself a chain fact. A record the reconciliation step
 * burned as "not applied" (R69 row 6) did not take effect: its tree moved on
 * through another authorization, proven from that one's own transaction.
 */
export function retirementOf(record: LeafRecord): { step: 'rejected' | 'failed' | 'expired'; sentence: string } {
  const note = record.note ?? '';
  if (note.startsWith('rejected before sending')) return { step: 'rejected', sentence: `${REJECTED_SENTENCE} ${note.slice('rejected before sending: '.length)}`.trim() };
  if (note.startsWith('failed on chain')) return { step: 'failed', sentence: `${FAILED_SENTENCE} ${note.slice('failed on chain: '.length)}`.trim() };
  if (note.startsWith('expired')) return { step: 'expired', sentence: EXPIRED_SENTENCE };
  if (note.startsWith(NOT_APPLIED_NOTE_PREFIX)) return { step: 'expired', sentence: NOT_APPLIED_SENTENCE };
  return { step: 'expired', sentence: note ? `${RETIRED_SENTENCE} (${note})` : RETIRED_SENTENCE };
}

/**
 * An unresolved key refresh (a leaf-255 recovery record), in words that follow the record's state exactly (docs/18 5.1
 * C): `reserved` was prepared and not signed; `signed` was not sent; `sent` has not taken effect, or, while the caller
 * still holds this session's landed-and-failed outcome for that very transaction (`failedOnChain`), landed and failed on
 * chain. Never "outcome unknown". Home shows it in its notice; `refreshNeeded` gives it as the reason, so Withdraw and
 * Swap run the refresh (which reuses the record) instead of reaching the signer's "Unresolved" refusal.
 */
export function pendingRefreshText(record: LeafRecord, failedOnChain: string | null = null): string {
  if (record.status === 'reserved') return 'The key refresh was prepared but not sent. Nothing else can be signed until it takes effect. Trying again signs the same refresh; no new key is used.';
  if (record.status === 'signed') return 'The key refresh was signed but not sent. Nothing else can be signed until it takes effect. Sending it again reuses the same signature; no new key is used.';
  if (failedOnChain !== null && record.txSignature === failedOnChain) return 'The key refresh landed and failed on chain. Sending it again reuses the same signature; no new key is used.';
  return 'The key refresh was sent and has not taken effect. Sending it again reuses the same signature; no new key is used.';
}

/** A signed action that did not land, with the key burned: the caller runs the key refresh (docs/08 5.5 step 4). `kind` says whether the attempt was refused before sending or failed on chain (R45). */
export class ActionFailed extends Error {
  constructor(
    readonly kind: FailureKind,
    readonly detail: string,
    readonly cause: unknown,
    readonly signature?: string,
  ) {
    super(kind === 'rejected' ? REJECTED_SENTENCE : FAILED_SENTENCE);
    this.name = 'ActionFailed';
  }
}

/** A signed action that was sent but not confirmed: the record stays "sent", nothing is burned, and `reconcile` resolves it later (R45). */
export class ActionUnknown extends Error {
  constructor(
    readonly signature: string,
    readonly detail: string,
    readonly cause: unknown,
  ) {
    super(UNKNOWN_SENTENCE);
    this.name = 'ActionUnknown';
  }
}

/** A key refresh that did not land; the recovery message is deterministic, so the same refresh can be tried again. */
export class RefreshFailed extends Error {
  constructor(
    readonly kind: FailureKind | 'unknown',
    readonly detail: string,
    readonly cause: unknown,
    readonly signature?: string,
  ) {
    super(detail);
    this.name = 'RefreshFailed';
  }
}

/** Section 7: the deployed program is not the one this build was made for. Raised before any key is reserved, so nothing is burned and no refresh is needed. */
export class NotTrusted extends Error {
  constructor(readonly note: string) {
    super(`This app will not sign against this program: ${note}. Verify shows the comparison; Settings can mark this session as development.`);
    this.name = 'NotTrusted';
  }
}

/** The key needs a refresh before this action (an expired marker, a burned key, a closed vault): raised before any key is reserved, so the caller runs the refresh and comes back (R44). */
export class RefreshRequired extends Error {
  constructor(readonly reason: string) {
    super(`${reason} The vault refreshes its key first; nothing was signed.`);
    this.name = 'RefreshRequired';
  }
}

/** The reviewed quote is older than `QUOTE_MAX_AGE_MS`: it is never signed silently; the caller fetches a fresh one, shows it, and asks for a new confirmation (R41). */
export class QuoteStale extends Error {
  constructor() {
    super('The reviewed quote is too old to sign. A fresh one is fetched, without using a key; review it and confirm again.');
    this.name = 'QuoteStale';
  }
}

/**
 * The gas tank cannot pay for this action: raised before any key is reserved (R49), and before a key refresh's
 * `recover()` (docs/18 5.1 A). `movable` is set when this device's older reserve, read at the time of the refusal, is
 * priced and its plan is Form A or B, so the panel can point to the move (docs/18 section 6); null otherwise, or when
 * that read failed.
 */
export class InsufficientGas extends Error {
  constructor(
    readonly cost: CostEstimate,
    readonly movable: OlderReserve | null = null,
  ) {
    super('The fee reserve cannot pay for this action. Nothing was signed.');
    this.name = 'InsufficientGas';
  }
}

/**
 * A legacy device's older fee reserve, as read for the notice, the review and the recheck (docs/18 section 6). `address`
 * is `VaultInfo.relayer`, or the address this session opened and recorded; null on a pre-R48 device until its envelope
 * is opened, and then `lamports` is 0. `current` is the derived reserve's balance (C) and `minimum` is F, read live.
 * `plan` is the table and the guard over those figures (sdk/ts/src/fee-reserve.ts `sweepPlan`). `priced` is false, with
 * `unpricedReason` in the user's words, when either account is not an empty System account: no sweep is offered or sent
 * then, whatever `plan` says. `pending` is true when the only reason it is not priced is a sweep awaiting its outcome
 * (`unpricedReason` is then that sentence): the page says it is waiting, not that the move is not priced.
 */
export interface OlderReserve {
  address: string | null;
  lamports: bigint;
  current: bigint;
  minimum: bigint;
  plan: SweepPlan;
  priced: boolean;
  unpricedReason: string | null;
  pending: boolean;
}

/** The recheck after the last wait did not reproduce the reviewed form, amount or fee (docs/18 section 6): nothing was sent, and the page shows the review again from `reading`. */
export class SweepChanged extends Error {
  constructor(readonly reading: OlderReserve) {
    super('The figures changed since the review, so nothing was sent. Review the move again.');
    this.name = 'SweepChanged';
  }
}

/**
 * Why no sweep is sent (docs/18 section 6): `none`, this device holds no older reserve key; `mismatch`, the opened key
 * is not the recorded address; `not-priced`, an account is not an empty System account; `blocked`, the guard holds;
 * `not-yet`, the derived reserve must be topped up first; `nothing`, the older reserve is empty; `pending`, a sweep of
 * this engine is running, or a sweep this session sent (in this or an earlier runtime, `pendingSweep`) has no known
 * outcome yet, so the older key is never moved twice at once. `reading` is the reading that refused, when there is one.
 */
export type SweepRefusal = 'none' | 'mismatch' | 'not-priced' | 'blocked' | 'not-yet' | 'nothing' | 'pending';
export class SweepRefused extends Error {
  constructor(
    readonly reason: SweepRefusal,
    readonly reading: OlderReserve | null,
    message: string,
  ) {
    super(message);
    this.name = 'SweepRefused';
  }
}

/**
 * A sweep stopped before its first send attempt (docs/18 section 6): nothing was sent. `cause` is what stopped it (the
 * passphrase, the envelope read, the network check, a read, the worker, the simulation, a lock). The message is the
 * cause's own, so a lock still reads "locked", and `describe` describes the cause. `SweepRefused` and `SweepChanged`
 * are thrown as they are; from the first attempt on, an error is thrown unwrapped, since the wire may have gone out.
 */
export class SweepNotSent extends Error {
  constructor(readonly cause: unknown) {
    super(cause instanceof Error ? cause.message : String(cause));
    this.name = 'SweepNotSent';
  }
}

/**
 * This browser's storage could not be read for the legacy envelope (docs/18 section 6): the device may hold an older
 * fee reserve key, or none. Raised before the network check and any balance read, so a page says the key could not be
 * read instead of saying that one exists. Nothing is sent.
 */
export class EnvelopeUnreadable extends Error {
  constructor(readonly cause: unknown) {
    super(`This device may hold an older fee reserve key, but it could not be read from this browser's storage (${cause instanceof Error ? cause.message : String(cause)}).`);
    this.name = 'EnvelopeUnreadable';
  }
}

/** Send out refused before anything is signed (docs/18 5.1 A, the Send out row; M18); the message says why, in the user's words. */
export class SendOutRefused extends Error {
  constructor(
    readonly reason: SendOutRefusal,
    readonly limits: SendOutLimits & { minimum: bigint },
    message: string,
  ) {
    super(message);
    this.name = 'SendOutRefused';
  }
}

/** What the fee reserve holds, with the engine's own figures for it (docs/18 5.1 A), all from one live read of F and the balance. */
export interface ReserveReading {
  lamports: bigint;
  /** F, the account minimum. */
  minimum: bigint;
  /** "About N actions": floor((B − F − K) / 15,000), or 0 below F + K. */
  actions: number;
  /** One plain action, Home's low-reserve threshold: F + 15,000 + K. */
  plainAction: bigint;
  /** The key refresh: F + 15,000. */
  refresh: bigint;
  /** The suggested top-up (F + K + 10 × 15,000): a suggestion, never a gate. */
  suggested: bigint;
}

/**
 * What Activate, or its Check again, found (docs/18 5.1 D). `landed`: this device's own Initialize is finalized and
 * successful, a finalized read shows the vault untouched on tree 0, and the tree-0 marker exists. `exists`: the fresh
 * read found a vault (or a closed one), so nothing was sent and no marker was created, renewed or deleted. `failed`: the
 * carrier landed and failed on chain. `unknown`: sent, with no evidence either way yet (a lost reply, a read error, no
 * finalization by the deadline, a finalized readback that could not be read). `not-seen`: the network has no record of
 * the held signature. `not-final`: confirmed, not finalized. `not-visible`: finalized and successful, but the finalized
 * read does not show a vault account of this program at the address yet (a node behind the one that answered the
 * status). `differs`: finalized and successful, and the finalized read shows a vault account of this program that is not
 * the untouched tree 0 of this genesis key. `none`: no attempt is held (a lock or reload dropped it). Only `landed`
 * writes a marker. The engine keeps the attempt after every step but `landed` and `failed`, so Check again stays open.
 */
export type ActivationStep = 'landed' | 'exists' | 'failed' | 'unknown' | 'not-seen' | 'not-final' | 'not-visible' | 'differs' | 'none';
export interface ActivationResult {
  step: ActivationStep;
  /** The carrier's signature, computed from its wire before the send; null when nothing was sent. */
  signature: string | null;
  detail: string | null;
}
/** The activation attempt the engine holds (5.1 D step 3): bound to this vault's address. */
export interface ActivationAttempt {
  vaultAddress: string;
  signature: string;
}

/**
 * The vault's input account holds less than the amount plus the treasury fee
 * a swap whose fee is paid from the input may take (`SwapPlan.maxIn`): the
 * fee transfer after the swap would fail, so the attempt would be refused in
 * the pre-flight check and retire a key. Raised before any key is reserved.
 */
export class InsufficientInput extends Error {
  constructor(
    readonly held: bigint,
    readonly needed: bigint,
  ) {
    super('The vault holds less than the amount plus the treasury fee this swap may spend. Nothing was signed; lower the amount and quote again.');
    this.name = 'InsufficientInput';
  }
}

/** A token account an action would create if it does not exist yet, with the words the cost line uses for it; its mint and token program decide its size, and so its rent (R52). */
export interface NewAccount {
  address: Uint8Array;
  label: string;
  mint: Uint8Array;
  tokenProgram: Uint8Array;
}

/**
 * What an action needs the gas tank to hold (docs/08 2, 5.7; R49; docs/18 5.1 A): its own account minimum F, which the
 * fee payer must keep unless it ends at exactly 0, the network fee, rent for each account the transaction creates
 * (sized by its mint, R52), and one plain action held back for a key refresh (K; none for the refresh itself). `total`
 * is the sum, from the one requirement function in sdk/ts/src/fee-reserve.ts; equality is enough.
 */
export interface CostEstimate {
  /** F: the rent-exempt minimum of a 0-byte account, read live; returned when the reserve is emptied. */
  minimum: bigint;
  fee: bigint;
  rent: { label: string; lamports: bigint; bytes: number }[];
  /** K, held for a key refresh. */
  reserve: bigint;
  total: bigint;
  /** The gas tank's balance at the time of the estimate. */
  tank: bigint;
  enough: boolean;
}

/** What a signed action needs beyond the signature: the header, aux and inner instructions the signer signs, the top-level instructions around Execute, the limits, and the accounts it may create. */
interface Built {
  header: ExecHeaderSpec;
  aux: AccountMetaSpec[];
  inners: InnerIx[];
  /** Instructions before Execute. */
  top: Instruction[];
  /** Instructions after Execute. */
  after?: Instruction[];
  /** Overrides `top`/`after`: the full body around the Execute instruction the signer produced. */
  compose?: (execute: Instruction) => Instruction[];
  limits: Limits;
  purpose: Purpose;
  newAccounts: NewAccount[];
}

export interface SwapRequest {
  inMint: string;
  outMint: string;
  /** Raw units of the input mint. */
  amount: bigint;
  slippageBps: number;
  /** BOUNDED mode (docs/08 5.5 step 4): the route may be swapped for a fresh one under the same signature. Off by default. */
  bounded: boolean;
}

/** Everything the review panel shows and the confirm step signs; no key is used to make one (docs/08 5.5 step 2). Confirm signs this plan and no other (R41). */
export interface SwapPlan {
  request: SwapRequest;
  ladder: LadderResult;
  expect: Expected;
  mints: TokenPrograms;
  /**
   * The side that pays the treasury fee, fixed by the two signed mints
   * (program/src/fee.rs `fee_side`): a USDC or USDT side (the output when
   * both are), else a SOL side, else the output. On the output side the fee
   * comes out of what arrives; on the input side it is paid from the input
   * account on top of what the swap spends, and the output arrives whole.
   */
  feeSide: FeeSide;
  /** base58: the mint the fee is paid in (the input mint on the input side, the output mint on the output side). */
  feeMint: string;
  /** Decimals of `feeMint`. */
  feeDecimals: number;
  /** At most this much leaves the input account: the amount on the output side; the amount plus the treasury fee on it on the input side (the bound the program checks, `maxInForRoute`). */
  maxIn: bigint;
  /** What the router quoted. */
  quotedOut: bigint;
  /** At least this much must arrive in the output account, or the vault refuses to settle (before the fee on the output side). */
  grossMin: bigint;
  /** The treasury fee the panel shows, in `feeMint`: on the output side its share of the gross minimum (more if more arrives); on the input side its share of the amount the swap spends. */
  fee: bigint;
  /** The largest figure on the panel, what the vault keeps at the minimum: `grossMin - fee` on the output side, all of `grossMin` on the input side. */
  netMin: bigint;
  routeLabels: string[];
  inDecimals: number;
  outDecimals: number;
  feeAccount: string;
  quotedAt: number;
  /** Ladder rungs skipped before one fit (docs/08 5.5 step 1: shown, never hidden). */
  skipped: { maxAccounts: number; reason: string }[];
  /** Set when this signature also installs the next key tree (the signer's rotation policy, `ROTATE_AT`): sized, shown and signed as one header (R43). */
  nextKey?: Uint8Array;
  rotates: boolean;
}

export interface SwapResult extends ActionResult {
  /** From the landed transaction's own balance changes and fee transfer (R46), or null when it could not be read. */
  receipt: SwapReceipt | null;
  receiptNote: string | null;
}

/** `VaultError::SettleFailed` (program/src/lib.rs): the route delivered less than the minimum. */
const SETTLE_FAILED = 14;
/** How old a reviewed quote may be at Confirm before it is refused and fetched again (docs/08 5.5 step 4, R41). */
export const QUOTE_MAX_AGE_MS = 25_000;

/**
 * A simulated failure raised by the route or a venue, not by the vault: the candidate for BOUNDED retry and for
 * excluding the venue this session. Only an instruction-level error can be one (docs/18 5.1 B, R78): an error of the
 * transaction itself (`InsufficientFundsForFee`, `InsufficientFundsForRent { account_index }`, any other) never is, so
 * it causes no re-quote, no `resend` and no venue exclusion (sdk/ts/src/relayer.ts `transactionError`).
 */
export function isRouteFailure(e: unknown): boolean {
  if (!(e instanceof RelayerError) || e.code !== 'SimulationFailed') return false;
  return isRouteFailureError(e);
}

export function isSettleRefusal(e: unknown): boolean {
  if (!(e instanceof RelayerError) || e.code !== 'SimulationFailed') return false;
  const t = transactionError(e);
  return t?.kind === 'instruction' && t.inner !== null && typeof t.inner === 'object' && (t.inner as { Custom?: unknown }).Custom === SETTLE_FAILED;
}

/** The base58 account keys of the message a chain error was raised against, kept beside the error so its account index can be named (5.1 B). */
const accountKeysOf = new WeakMap<object, string[]>();
function withAccounts<E extends object>(e: E, keys: string[]): E {
  accountKeysOf.set(e, keys);
  return e;
}
/** A v1 wire's account keys: the count at byte 41, the keys from byte 42 (sdk/ts/src/v1.ts serializeV1). */
function accountKeysOfWire(wire: Uint8Array): string[] {
  const n = wire[41] ?? 0;
  return Array.from({ length: n }, (_, i) => toBase58(wire.subarray(42 + 32 * i, 74 + 32 * i)));
}

/**
 * A transaction-level error in the user's words, with the raw error kept (docs/18 5.1 B). `InsufficientFundsForFee`:
 * the fee reserve could not pay the fee. `InsufficientFundsForRent` at index 0: the fee reserve, which would have been
 * left below its account minimum; at any other index: that account, named from the message's keys, and not the reserve.
 * `payer` names account 0: the fee reserve, except in a Form A sweep, where the older key pays (docs/18 section 6).
 */
export function transactionErrorText(t: Extract<TransactionError, { kind: 'transaction' }>, keys?: string[], payer = 'the fee reserve'): string {
  const raw = JSON.stringify(t.raw);
  const Payer = payer.charAt(0).toUpperCase() + payer.slice(1);
  if (t.name === 'InsufficientFundsForFee') return `${Payer} could not pay the network fee (${raw}).`;
  if (t.name === 'InsufficientFundsForRent' && t.accountIndex !== undefined) {
    if (t.accountIndex === 0) return `${Payer} would have been left below its account minimum, so the network refused the transaction (${raw}; account 0 is ${payer}).`;
    const who = keys?.[t.accountIndex];
    return `The account ${who ?? `at index ${t.accountIndex}`} would have been left holding less than its rent-exempt minimum, so the network refused the transaction; that account, not ${payer}, is the cause (${raw}; account ${t.accountIndex}).`;
  }
  return `The network refused the transaction itself: ${t.name} (${raw}).`;
}

/** Who pays a sweep's fee, account 0 of its transfer (docs/18 section 6, the table): Form A the older key, Form B your fee reserve. */
function sweepPayer(form: 'A' | 'B'): string {
  return form === 'A' ? 'the older fee reserve' : 'the fee reserve';
}

/**
 * A sweep's error in the user's words: `describe`, except that an error of the transaction itself names account 0 by
 * the form (5.1 B; docs/18 section 6), since `describe` takes account 0 to be the fee reserve, which only Form B's is.
 */
export function describeSweep(e: unknown, form: 'A' | 'B'): string {
  const inner = e instanceof SweepNotSent ? e.cause : e;
  if (inner instanceof RelayerError && (inner.code === 'SimulationFailed' || inner.code === 'ConfirmFailed')) {
    const t = transactionError(inner);
    if (t?.kind === 'transaction') return transactionErrorText(t, accountKeysOf.get(inner), sweepPayer(form));
  }
  return describe(e);
}

/**
 * The signature of a sweep that threw after its first send attempt (docs/18 section 6), kept beside the error as
 * `accountKeysOf` keeps its keys, so the error stays the one `sendSignedV1` threw and the page can still take the
 * outcome the session was given (`Session.takeSweepOutcome`).
 */
const sweepSignatureOf = new WeakMap<object, string>();
export function sentSweepSignature(e: unknown): string | null {
  return typeof e === 'object' && e !== null ? (sweepSignatureOf.get(e) ?? null) : null;
}

/** Lamports as SOL, for a sentence (up to nine decimals, trailing zeros dropped). */
function solText(lamports: bigint): string {
  const sign = lamports < 0n ? '-' : '';
  const v = lamports < 0n ? -lamports : lamports;
  const frac = (v % 1_000_000_000n).toString().padStart(9, '0').replace(/0+$/, '');
  return `${sign}${v / 1_000_000_000n}${frac ? `.${frac}` : ''} SOL`;
}

/** Why Send out refused, in the user's words (5.1 A). */
function sendOutText(reason: SendOutRefusal, r: { balance: bigint; minimum: bigint; fee: bigint; maxPartial: bigint | null; destinationLamports: bigint }): string {
  switch (reason) {
    case 'nothing':
      return 'Enter an amount to send. Nothing was signed.';
    case 'below-minimum':
      return `The fee reserve holds ${solText(r.balance)}, less than its account minimum of ${solText(r.minimum)} plus the ${solText(r.fee)} fee, so nothing can be sent out. Nothing was signed.`;
    case 'over-balance':
      return `That is more than the fee reserve holds after the ${solText(r.fee)} fee. Nothing was signed.`;
    case 'leaves-below-minimum':
      return `That would leave the fee reserve below its account minimum of ${solText(r.minimum)}. Send at most ${solText(r.maxPartial ?? 0n)}, or everything but the fee. Nothing was signed.`;
    case 'destination-below-minimum':
      return `The destination holds ${solText(r.destinationLamports)}, less than an account's minimum of ${solText(r.minimum)}, so it must end with at least that much: send at least ${solText(r.minimum - r.destinationLamports)}. Nothing was signed.`;
  }
}

/**
 * Why an account read for the sweep is not priced (docs/18 section 6, "Only empty System accounts are priced"), or
 * null for an empty System account. An absent account is one: the table prices it at 0 lamports.
 */
function unpricedText(account: OnChainAccount | null, whose: string, move: string): string | null {
  if (!account) return null;
  const systemOwned = account.owner.length === 32 && account.owner.every((b) => b === 0);
  if (!systemOwned) return `${whose} account is owned by ${toBase58(account.owner)}, not the System program, so the app does not price a move ${move} it. Nothing is sent.`;
  if (account.data.length > 0) return `${whose} account holds ${account.data.length} bytes of data, so the app does not price a move ${move} it. Nothing is sent.`;
  return null;
}

/** The reading's reason while a sweep awaits its outcome (docs/18 section 6): no second move is offered. */
export const SWEEP_PENDING_REASON = 'A move from the older fee reserve is awaiting confirmation, so no other move is offered until its outcome is known, which happens on its own. Nothing is sent.';
/** The refusal of a second sweep while one awaits its outcome. */
const SWEEP_PENDING_REFUSAL = 'A move from the older fee reserve is awaiting confirmation. Another move waits until its outcome is known, which happens on its own. Nothing was sent.';
/** The watch of a sweep with no known outcome polls as `Rpc.confirm` does (sdk/ts/src/relayer.ts). */
const SWEEP_WATCH_MS = 1_500;
/**
 * A blockhash's lifetime in blocks: `getLatestBlockhash` reports `lastValidBlockHeight` as the current block height
 * plus this (Agave's MAX_PROCESSING_AGE; the shim reports slot + 150, tools/rpc-shim/src/main.rs).
 */
const BLOCKHASH_LIFETIME = 150n;

/** A finished wire's transaction signature: the first of its signatures, the payer's; byte 1 is the signature count (sdk/ts/src/v1.ts serializeV1). */
function payerSignatureOfWire(wire: Uint8Array): string {
  const n = Math.max(1, wire[1] ?? 1);
  const at = wire.length - 64 * n;
  return toBase58(wire.subarray(at, at + 64));
}

/** `describe`'s sentence for a failed confirmation that names no reason: the failed outcome's own sentence says as much. */
const GENERIC_CONFIRM_FAILED = 'The transaction landed but failed on chain.';

/**
 * A sent sweep that did not confirm, in the user's words, for `SweepOutcome.detail` (docs/18 section 6): failed on
 * chain, its payer (account 0, by the form) paid the fee and nothing else moved; anything else (the confirmation gave
 * up, the send never answered, a lock stopped a retry) may still have landed, which the older reserve's balance settles.
 */
function sweepOutcomeDetail(e: unknown, form: 'A' | 'B'): string {
  if (e instanceof RelayerError && e.code === 'ConfirmFailed') {
    const payer = sweepPayer(form);
    const why = describeSweep(e, form);
    const reason = why.trim() === GENERIC_CONFIRM_FAILED ? '' : ` ${sentenceOf(why)}`;
    return `${payer.charAt(0).toUpperCase()}${payer.slice(1)} paid its network fee and nothing else moved.${reason}`;
  }
  const why = e instanceof Error && e.message === 'locked' ? 'The app locked while the transfer was being sent, after a first attempt.' : sentenceOf(describeSweep(e, form));
  return `${why} The transfer may still land; the older fee reserve's balance shows whether it did.`;
}

function sentenceOf(s: string): string {
  const t = s.trim();
  return /[.!?]$/.test(t) ? t : `${t}.`;
}

/** A sweep refusal in the user's words (docs/18 section 6). */
function sweepRefusalText(reason: Exclude<SweepRefusal, 'none' | 'mismatch' | 'not-priced' | 'pending'>, plan: SweepPlan): string {
  switch (reason) {
    case 'blocked':
      return "An earlier action's outcome is not known yet, and the older fee reserve may still be paying for it. The move waits until the overview's next read of the vault settles that action: reload the overview, or open it again. Nothing was sent.";
    case 'not-yet':
      return `Add at least ${solText(plan.form === 'not-yet' ? plan.add : 0n)} to your fee reserve first, from any wallet, then move. Nothing was sent.`;
    case 'nothing':
      return 'The older fee reserve is empty, so there is nothing to move. Nothing was sent.';
  }
}

const JUPITER_B58 = 'JUP6LkbZbjS1jKKwapdHNy74zcZ3tLUZoi5QNyVTaV4';

/** The innermost program the simulation logs blame: the first "Program X failed: ..." line, since a failing CPI is logged before every program above it fails in turn. Null when unknown. */
export function failingProgramOf(e: unknown): string | null {
  if (!(e instanceof RelayerError) || e.code !== 'SimulationFailed') return null;
  const logs = ((e.detail as { logs?: string[] } | undefined)?.logs ?? []).filter((l) => / failed: /.test(l));
  const first = logs[0];
  const m = first ? /^Program (\S+) failed/.exec(first) : null;
  return m?.[1] ?? null;
}

/** A venue (an AMM program), not Jupiter itself and not the vault, refused the trade: that is what session exclusion is for (docs/08 5.5 step 4, the Quantum case). */
export function isVenueRejection(e: unknown, programIdB58: string): boolean {
  if (!isRouteFailure(e)) return false;
  const program = failingProgramOf(e);
  return program !== null && program !== JUPITER_B58 && program !== programIdB58;
}

/** The key refresh's steps (docs/08 5.8); `failed` carries which kind (`rejected`: not sent; `failed`: landed and failed on chain), `unknown` a signature to check. */
export type RefreshStep = 'preparing' | 'sending' | 'landed' | 'failed' | 'unknown';
export interface RefreshProgress {
  step: RefreshStep;
  signature?: string;
  error?: string;
  kind?: FailureKind | 'unknown';
}

/** What Home shows about the key line (docs/08 4, 5.3, 5.8). */
export interface KeyStatus {
  treeIndex: number;
  usedLeaves: number;
  actionsLeft: number;
  mode: 'normal' | 'recovery-only';
  /** Why a refresh is pending, in the user's words, or null while the tree is normal. */
  refreshReason: string | null;
  marker: TreeMarker | undefined;
  markerExpiresInSlots: bigint | null;
  /** Records whose outcome is not known yet (sent, not confirmed): nothing new signs while any exist (R45). */
  unresolved: LeafRecord[];
}

// The pipeline's own outcome markers, turned into the public errors by `outcome()`.
class Rejected {
  constructor(readonly cause: unknown) {}
}
class FailedOnChain {
  constructor(
    readonly signature: string,
    readonly cause: unknown,
  ) {}
}
class Unknown {
  constructor(
    readonly signature: string,
    readonly cause: unknown,
  ) {}
}

/** The transaction's own signature: a v1 wire is the message followed by its signatures, and the relayer signs alone (sdk/ts/src/relayer.ts `signV1`). */
function signatureOfWire(wire: Uint8Array): string {
  return toBase58(wire.subarray(wire.length - 64));
}

export class Engine {
  readonly rpc: Rpc;
  readonly store: IndexedDbLedgerStore;
  readonly backend: WorkerBackend;
  readonly relayer: RelayerSigner;
  readonly programId: Uint8Array;
  readonly info: VaultInfo;
  readonly vaultAddressBytes: Uint8Array;
  readonly relayerAddress: string;
  readonly clock = new SlotClock();
  /** Venues that rejected the vault this session (docs/08 5.5 step 4); merged into every quote's exclusions and listed on the swap page. */
  readonly sessionExclusions = new Set<string>();
  private signer: VaultSigner | null = null;
  /** The open-and-resolve in flight (R69, docs/17 section 6): concurrent `signerFor` callers share it, so the signer is opened once; cleared when it settles. */
  private opening: Promise<VaultSigner> | null = null;
  /**
   * A `sweep` of this engine runs, from its call until it returns or throws (docs/18 section 6). Before its first send
   * attempt this is the only latch; from that attempt the session's (`pendingSweepOf`) holds until the outcome is
   * known. While either is set a second `sweep` refuses ('pending') and `olderReserve` waits, so the older key is never
   * moved twice at once.
   */
  private sweeping = false;
  /** The signature this engine's `watchSweep` is polling, so one engine never runs two watches of one sweep. */
  private watching: string | null = null;
  /**
   * The session's evidence reader for the reconciliation step (R77, docs/17 section 6): one per engine, so one per
   * unlocked session (the session builds an engine at every unlock and drops it at Lock), with its pass in flight,
   * its memo of final answers and its cap flag. Its reads refuse "locked" once the engine has closed, so no read
   * starts after a Lock.
   */
  private readonly earlier: EarlierReads;
  /** Set synchronously by `close()` (the session's lock, R68): from then on `signerFor` and `sync` refuse with "locked" rather than open a fresh signer over a closing store and wiped worker. */
  private closed = false;
  /**
   * The current activation attempt (docs/18 5.1 D step 3): the Initialize carrier's signature, computed from its wire
   * before the send and bound to this vault's address. The engine holds it, not the page: it is replaced by the next
   * attempt, dropped once its outcome is final, and dropped at close (Lock) with the engine, so a reload or a lock
   * leaves the device recovery-only. No storage holds it.
   */
  private activation: ActivationAttempt | null = null;
  private readonly settingsOf: () => AppSettings;
  private readonly jupiterKeyOf: () => string | null;
  private readonly developmentOf: () => boolean;
  private readonly client: WorkerClient;
  private readonly legacyEnvelopeOf: () => Promise<SealedEnvelope | undefined>;
  private readonly recordLegacyAddressOf: (address: string) => Promise<void>;
  private readonly onSweepOutcomeOf: ((outcome: SweepOutcome) => void) | null;
  private readonly pendingSweepOf: () => PendingSweep | null;
  private readonly setPendingSweepOf: (sweep: PendingSweep | null) => void;
  /**
   * The older reserve's address (docs/18 section 6): `VaultInfo.relayer` as this unlock read it, or, on a pre-R48
   * device, the address this session's first opening recorded. A stored value that is not a string counts as none
   * recorded, and the writer then refuses to record over it.
   */
  private legacyAddress: string | null;
  private decimalsCache = new Map<string, number>();

  constructor(d: EngineDeps) {
    this.rpc = d.rpc;
    this.store = d.store;
    this.backend = d.backend;
    this.relayer = d.relayer;
    this.programId = d.programId;
    this.info = d.info;
    this.vaultAddressBytes = addressBytes(address(d.info.address));
    this.relayerAddress = toBase58(d.relayer.publicKey);
    this.settingsOf = d.settings;
    this.jupiterKeyOf = d.jupiterApiKey;
    this.developmentOf = d.development;
    this.client = d.client;
    this.legacyEnvelopeOf = d.legacyEnvelope;
    this.recordLegacyAddressOf = d.recordLegacyAddress;
    this.onSweepOutcomeOf = d.onSweepOutcome ?? null;
    this.pendingSweepOf = d.pendingSweep;
    this.setPendingSweepOf = d.setPendingSweep;
    this.legacyAddress = typeof d.info.relayer === 'string' ? d.info.relayer : null;
    this.earlier = new EarlierReads({
      statuses: async (signatures) => {
        this.assertOpen();
        return this.rpc.signatureStatuses(signatures);
      },
      transaction: async (signature) => {
        this.assertOpen();
        return this.rpc.getTransactionJson(signature);
      },
      capMs: EARLIER_READ_CAP_MS,
    });
  }

  /** One `getSlot`, recorded on the slot clock. */
  async observeSlot(): Promise<number> {
    const slot = await this.rpc.getSlot();
    this.clock.observe(slot);
    return slot;
  }

  // ------------------------------------------------------------ reading

  async readChain(): Promise<ChainState> {
    const account = await this.rpc.getAccountInfo(this.info.address);
    if (!account) return { kind: 'absent' };
    try {
      const snap = snapshot(this.programId, account.owner, account.data);
      return { kind: snap.tag === 2 ? 'tombstone' : 'vault', account, snap };
    } catch {
      const systemOwned = account.owner.length === 32 && account.owner.every((b) => b === 0);
      if (systemOwned && account.data.length === 0) return { kind: 'prefunded', account };
      return { kind: 'foreign', account };
    }
  }

  /** F, the fee reserve's account minimum: the rent-exempt minimum of a 0-byte account, read live (docs/18 5.1 A). */
  async reserveMinimum(): Promise<bigint> {
    return this.rpc.getMinimumBalanceForRentExemption(0);
  }

  /**
   * The fee reserve's balance and the engine's figures for it, from one live read of F and the balance (5.1 A). The
   * network check runs first (docs/18 2.5), so a page that shows the reserve's address as a funding destination only
   * after this resolves never shows it on the wrong network; it throws `WrongNetwork` there.
   */
  async gasTank(): Promise<ReserveReading> {
    await this.assertDeployment();
    const minimum = await this.reserveMinimum();
    const lamports = await this.rpc.getBalance(this.relayerAddress);
    return {
      lamports,
      minimum,
      actions: actionCount(lamports, minimum),
      plainAction: plainActionRequirement(minimum).total,
      refresh: refreshRequirement({ minimum, fee: feeOf(PLAIN_LIMITS) }).total,
      suggested: suggestedTopUp(minimum),
    };
  }

  async vaultLamports(): Promise<{ lamports: bigint; rentFloor: bigint; spendable: bigint }> {
    const [account, rentFloor] = await Promise.all([this.rpc.getAccountInfo(this.info.address), this.rpc.getMinimumBalanceForRentExemption(VAULT_LEN)]);
    const lamports = account?.lamports ?? 0n;
    return { lamports, rentFloor, spendable: lamports > rentFloor ? lamports - rentFloor : 0n };
  }

  /**
   * The signer over the vault as it stands on chain; opened once, then kept in sync by `sync()`. Throws `KeyMismatch`
   * if the chain's key is not this phrase's. The signer is handed out only after the reconciliation step has run on
   * it (R69), so every page that gets it here sees the resolved ledger; concurrent callers share one open-and-resolve
   * in flight, and a Lock during it refuses every one of them "locked".
   */
  async signerFor(state: ChainState & { kind: 'vault' | 'tombstone' }): Promise<VaultSigner> {
    if (this.closed) throw new Error('locked');
    if (this.signer && !this.signer.isClosing) return this.signer;
    if (!this.opening) {
      const opening = this.openSigner(state).finally(() => {
        if (this.opening === opening) this.opening = null;
      });
      this.opening = opening;
    }
    return this.opening;
  }

  private async openSigner(state: ChainState & { kind: 'vault' | 'tombstone' }): Promise<VaultSigner> {
    const currentSlot = BigInt(await this.rpc.getSlot());
    if (this.closed) throw new Error('locked');
    const signer = await VaultSigner.open({ store: this.store, backend: this.backend, programId: this.programId, vaultAddress: this.vaultAddressBytes, owner: state.account.owner, accountData: state.account.data, currentSlot });
    // A close() that landed during the open has already passed `this.signer`: refuse this one rather than hand it out
    // (R68). Its close is not awaited: the session's teardown is already wiping (and may have ended) the worker, whose
    // 'wipe' clears the tree this open derived, and a wipeTree sent to an ended worker would never answer.
    if (this.closed) {
      void signer.close().catch(() => {});
      throw new Error('locked');
    }
    // R69: reconcile the records on trees the chain has left, with the account bytes and slot the open used. An error
    // other than a lock (an RPC failure, a store error, StaleSlot) leaves today's behaviour: the signer is handed out.
    try {
      await this.resolveEarlier(signer, state.account.data, currentSlot);
    } catch {
      /* the check below decides; a lock is never swallowed */
    }
    // Checked whatever the step threw: a Lock during the step refuses this signer as the check above does.
    if (this.closed || signer.isClosing) {
      void signer.close().catch(() => {});
      throw new Error('locked');
    }
    this.signer = signer;
    return signer;
  }

  /**
   * The reconciliation step (R69; docs/17 section 6): a record left `sent` with a `nextKey` on a tree the chain has
   * left is resolved from its own saved transaction, by the signer's `resolveEarlier`. With no such record it reads
   * nothing. Otherwise it asks the session's evidence reader for each one's (record key, `txSignature`) pair, which
   * answers from its memo, from a pass already in flight, or from a pass of its own, or answers "capped", and then
   * nothing is written. Evidence that arrives after a Lock is never handed to the signer.
   */
  private async resolveEarlier(signer: VaultSigner, accountData: Uint8Array, currentSlot: bigint): Promise<void> {
    const pairs = (await signer.earlierCandidates()).filter((r) => r.status === 'sent' && r.txSignature !== undefined).map((r) => ({ key: r.key, txSignature: r.txSignature! }));
    if (pairs.length === 0) return;
    const evidence = await this.earlier.read(pairs);
    if (evidence === 'capped') return;
    if (this.closed || signer.isClosing) throw new Error('locked');
    await signer.resolveEarlier({ evidence, accountData, currentSlot });
  }

  /** Refuse with "locked" once `close()` has run (the session's lock, R68). */
  private assertOpen(): void {
    if (this.closed) throw new Error('locked');
  }

  async sync(): Promise<ChainState> {
    if (this.closed) throw new Error('locked');
    const state = await this.readChain();
    // The signer is read once: a lock during the slot read (R68) sets this.signer to null, and the engine then
    // refuses "locked" as it does everywhere else, never a TypeError and never a sync on a closed signer.
    const signer = this.signer;
    if ((state.kind === 'vault' || state.kind === 'tombstone') && signer && !signer.isClosing) {
      const slot = BigInt(await this.rpc.getSlot());
      if (this.closed || signer.isClosing) throw new Error('locked');
      await signer.sync(state.account.data, slot);
      // R69: the step again, on the bytes and slot this sync judged; errors other than a lock leave the sync as it was.
      try {
        await this.resolveEarlier(signer, state.account.data, slot);
      } catch {
        /* the check below decides; a lock is never swallowed */
      }
      if (this.closed || signer.isClosing) throw new Error('locked');
    }
    return state;
  }

  /**
   * Why the next action must start with a key refresh, in the user's words, or null while the tree is signable (docs/08
   * 5.3, 5.8; R44). Read fresh from the ledger and the chain's slot every time. An unresolved key refresh on this tree
   * (its leaf-255 recovery record, reserved, signed or sent) is a reason too, in 5.1 C's words: the signer refuses every
   * ordinary authorization until it takes effect (signer.ts `Unresolved`), so Withdraw and Swap run the refresh, which
   * reuses the record under its own requirement (F + 15,000), instead of signing nothing and calling it an unknown outcome.
   */
  async refreshNeeded(signer: VaultSigner): Promise<string | null> {
    const marker = await this.store.getMarker(signer.vaultIdHex, signer.treeIndex);
    const slot = BigInt(await this.rpc.getSlot());
    const records = await this.store.listRecords(signer.vaultIdHex, signer.treeIndex);
    const burned = records.some((r) => r.status === 'burned');
    const pendingRecovery = records.find((r) => r.purpose === 'recovery' && UNRESOLVED.includes(r.status));
    if (signer.chain.tag === 2) return 'The vault is closed on chain; reopening it refreshes the key.';
    if (pendingRecovery) return pendingRefreshText(pendingRecovery);
    if (burned) return 'An attempt did not land, so the vault refreshes its key before anything else.';
    if (!marker) return 'This device has not held this key before, so the vault refreshes it first.';
    if (BigInt(marker.validUntilSlot) < slot) return 'This device has been away for a while, so the vault refreshes its key first.';
    return null;
  }

  async keyStatus(signer: VaultSigner): Promise<KeyStatus> {
    const marker = await this.store.getMarker(signer.vaultIdHex, signer.treeIndex);
    const slot = BigInt(await this.rpc.getSlot());
    const refreshReason = await this.refreshNeeded(signer);
    return {
      treeIndex: signer.treeIndex,
      usedLeaves: signer.usedCount,
      actionsLeft: Math.max(0, ROTATE_AT - signer.usedCount),
      mode: signer.mode,
      refreshReason,
      marker,
      markerExpiresInSlots: marker ? BigInt(marker.validUntilSlot) - slot : null,
      unresolved: await signer.pending(),
    };
  }

  async history(signer: VaultSigner): Promise<LeafRecord[]> {
    const rows: LeafRecord[] = [];
    for (let t = signer.treeIndex; t >= Math.max(0, signer.treeIndex - 8); t--) rows.push(...(await this.store.listRecords(signer.vaultIdHex, t)));
    return rows.sort((a, b) => b.updatedAt - a.updatedAt);
  }

  /**
   * "pending.json" (docs/08 5.9): the unresolved records of the current tree and nothing else, in particular no tree
   * marker. The note no longer promises a refresh on the importing device unconditionally (R76, docs/17 case h): an
   * imported `sent` record carrying `nextKey` is judged by the transaction it names, and when that transaction landed
   * the reconciliation step can earn the importing device a marker under M1-M6, as it can for a raw copy.
   */
  async exportPending(signer: VaultSigner): Promise<{ vault: string; treeIndex: number; records: LeafRecord[]; note: string }> {
    const records = await exportPending(this.store, signer.vaultIdHex, signer.treeIndex);
    return { vault: this.info.address, treeIndex: signer.treeIndex, records, note: EXPORT_NOTE };
  }

  /**
   * Resolve the records whose outcome was unknown when they were left
   * (R45): a transaction the chain reports as failed retires its key now
   * rather than at expiry; a landed one is marked landed by the sync (its
   * leaf shows as used on chain); one that never landed is burned by the
   * sync once its expiry has passed. A key refresh's own record is left to
   * the sync alone: its message never expires and can be re-sent as is.
   * Returns what is still unresolved afterwards.
   */
  async reconcile(signer: VaultSigner): Promise<LeafRecord[]> {
    for (const r of await signer.pending()) {
      if (r.status !== 'sent' || !r.txSignature || r.purpose === 'recovery') continue;
      const status = await this.rpc.signatureStatus(r.txSignature);
      if (status?.err) {
        try {
          await signer.markBurned(r.leaf, `failed on chain: ${JSON.stringify(status.err)}`);
        } catch {
          /* the sync below applies its own rules regardless */
        }
      }
    }
    await this.sync();
    return signer.pending();
  }

  // ------------------------------------------------------------- guards

  /**
   * docs/18 2.5, the network check: the selected RPC's `getGenesisHash` must equal the cluster tag of the deployment
   * this build was made for (deployment.ts), or this throws `WrongNetwork` (a failed read too). It runs before every
   * action that signs (in `guard`, `activate`, `refreshKey`, `sendGas`, and the sweep's recheck) and before `gasTank()`
   * and `olderReserve()` read a balance. Read per call, never cached (R42); not skipped in development mode, unlike
   * `assertTrustedProgram`.
   */
  async assertDeployment(): Promise<void> {
    await checkDeployment(this.rpc);
  }

  /**
   * Section 7, phishing: the app signs only against a program whose deployed
   * hash equals the one this build was made for, unless this session is
   * marked development. Read from the RPC before every action that signs or
   * initialises, never cached (R42): a redeploy after one successful action
   * is caught by the next. This is an observation of the account as the RPC
   * reports it, not an atomic pin: an upgrade landing between the check and
   * the send is not excluded by it.
   */
  async assertTrustedProgram(): Promise<void> {
    if (this.developmentOf()) return;
    const h = await deployedProgramHash(this.rpc, toBase58(this.programId));
    const note = h.error ?? (!h.applies ? 'the program account is not in the upgradeable loader layout, so its hash cannot be read' : h.matches ? '' : `deployed ${h.sha256?.slice(0, 16)}… differs from the published hash${h.tailIsZero === false ? ', and bytes sit beyond the artifact' : ''}`);
    if (!(h.applies && h.matches)) throw new NotTrusted(note);
  }

  /** Before any key is reserved: the network check (docs/18 2.5), the program check and the refresh check (R42, R44). */
  private async guard(signer: VaultSigner): Promise<void> {
    await this.assertDeployment();
    await this.assertTrustedProgram();
    const reason = await this.refreshNeeded(signer);
    if (reason) throw new RefreshRequired(reason);
  }

  /**
   * What an action needs the gas tank to hold (docs/08 5.7; R49; docs/18 5.1 A, R78): the fee reserve's own account
   * minimum F, read live (the payer must keep at least F after the fee and at the end, unless it ends at exactly 0),
   * the base fee of its one signature plus its priority fee, rent for each account among `newAccounts` that does not
   * exist yet, sized by what its mint forces on it (R52: a Token-2022 transfer-fee mint's account is 182 bytes, not
   * 165), and one plain action in reserve so a key refresh after a failed attempt can still be paid for. Read fresh,
   * including the tank's balance; the sum is sdk/ts/src/fee-reserve.ts `ordinaryRequirement`, and equality is enough.
   */
  async estimateCost(limits: Limits, newAccounts: NewAccount[]): Promise<CostEstimate> {
    const fee = feeOf(limits);
    const rent: { label: string; lamports: bigint; bytes: number }[] = [];
    const rentByLen = new Map<number, bigint>();
    for (const a of newAccounts) {
      if (await this.rpc.getAccountInfo(toBase58(a.address))) continue;
      const bytes = await this.tokenAccountLen(a.mint, a.tokenProgram);
      let lamports = rentByLen.get(bytes);
      if (lamports === undefined) {
        lamports = await this.rpc.getMinimumBalanceForRentExemption(bytes);
        rentByLen.set(bytes, lamports);
      }
      rent.push({ label: a.label, lamports, bytes });
    }
    const minimum = await this.reserveMinimum();
    const req = ordinaryRequirement({ minimum, fee, rents: rent.map((r) => r.lamports) });
    const tank = await this.rpc.getBalance(this.relayerAddress);
    return { minimum, fee, rent, reserve: req.hold, total: req.total, tank, enough: tank >= req.total };
  }

  /** One plain action, Home's low-reserve threshold and its panel (5.1 A): F + 15,000 + K, with the live balance. */
  async plainActionCost(): Promise<CostEstimate> {
    return this.estimateCost(PLAIN_LIMITS, []);
  }

  /** The key refresh (5.1 A), checked before `recover()` and by Restore's gate: F + 15,000, with no hold, and the live balance. */
  async refreshCost(): Promise<CostEstimate> {
    const minimum = await this.reserveMinimum();
    const fee = feeOf(PLAIN_LIMITS);
    const req = refreshRequirement({ minimum, fee });
    const tank = await this.rpc.getBalance(this.relayerAddress);
    return { minimum, fee, rent: [], reserve: req.hold, total: req.total, tank, enough: tank >= req.total };
  }

  /** Activation (5.1 A): F + f + K + the vault's actual rent shortfall, rent(128) less what its address already holds, from `state` or a fresh read. */
  async activationCost(state?: ChainState): Promise<CostEstimate> {
    const s = state ?? (await this.readChain());
    const held = s.kind === 'absent' ? 0n : s.account.lamports;
    const minimum = await this.reserveMinimum();
    const vaultRent = await this.rpc.getMinimumBalanceForRentExemption(VAULT_LEN);
    // activationLimits changes only the compute-unit limit, so the fee is a plain action's
    const fee = feeOf(PLAIN_LIMITS);
    const req = activationRequirement({ minimum, fee, vaultRent, vaultLamports: held });
    const tank = await this.rpc.getBalance(this.relayerAddress);
    const rent = req.rents > 0n ? [{ label: 'the vault account', lamports: req.rents, bytes: VAULT_LEN }] : [];
    return { minimum, fee, rent, reserve: req.hold, total: req.total, tank, enough: tank >= req.total };
  }

  /** What Send out can move now (5.1 A): the balance, F, the fee and the two limits that follow from them. */
  async sendOutRule(): Promise<{ balance: bigint; minimum: bigint; fee: bigint } & SendOutLimits> {
    const minimum = await this.reserveMinimum();
    const balance = await this.rpc.getBalance(this.relayerAddress);
    const fee = feeOf(PLAIN_LIMITS);
    return { balance, minimum, fee, ...sendOutLimits(balance, minimum, fee) };
  }

  /**
   * The size of the token account `tokenProgram` would create for `mint`,
   * from the mint account itself. A mint that cannot be read, has a
   * malformed Token-2022 layout, or carries an extension the SDK's table
   * does not know is not priced by a guess: `MintUnpriceable` fails the
   * estimate, so the review panel shows the reason and Confirm stays
   * disabled (R58). SPL Token accounts need no mint read: they are always
   * 165 bytes.
   */
  private async tokenAccountLen(mint: Uint8Array, tokenProgram: Uint8Array): Promise<number> {
    const program = toBase58(tokenProgram);
    if (program === toBase58(TOKEN_PROGRAM)) return associatedTokenAccountLen(program, new Uint8Array(0));
    const account = await this.rpc.getAccountInfo(toBase58(mint));
    if (!account) throw new MintUnpriceable('missing');
    return associatedTokenAccountLen(program, account.data);
  }

  async swapCost(plan: SwapPlan): Promise<CostEstimate> {
    const out = symbolOf(plan.request.outMint);
    const inn = symbolOf(plan.request.inMint);
    const { outMint, outTokenProgram, inMint, inTokenProgram } = plan.mints;
    // The treasury's account is the fee mint's: the input mint's when the input side pays (the transaction creates that one).
    const paying = feeMint(plan.mints);
    return this.estimateCost(LADDER_LIMITS, [
      { address: plan.expect.outAta, label: `the vault's ${out} account`, mint: outMint, tokenProgram: outTokenProgram },
      { address: plan.expect.inAta, label: `the vault's ${inn} account`, mint: inMint, tokenProgram: inTokenProgram },
      { address: plan.expect.feeAccount, label: `the treasury's ${symbolOf(toBase58(paying.mint))} account`, mint: paying.mint, tokenProgram: paying.tokenProgram },
    ]);
  }

  async withdrawNativeCost(): Promise<CostEstimate> {
    return this.estimateCost(PLAIN_LIMITS, []);
  }

  async withdrawTokenCost(destinationOwner: Uint8Array, mint: Uint8Array, tokenProgram: Uint8Array): Promise<CostEstimate> {
    const destAta = await ataAddress(destinationOwner, mint, tokenProgram);
    return this.estimateCost(TOKEN_TOUCHING_LIMITS, [{ address: destAta, label: `the destination's ${symbolOf(toBase58(mint))} account`, mint, tokenProgram }]);
  }

  async unwrapCost(): Promise<CostEstimate> {
    return this.estimateCost(TOKEN_TOUCHING_LIMITS, []);
  }

  async wrapCost(): Promise<CostEstimate> {
    const wsolAta = await ataAddress(this.vaultAddressBytes, WSOL_MINT, TOKEN_PROGRAM);
    return this.estimateCost(TOKEN_TOUCHING_LIMITS, [{ address: wsolAta, label: "the vault's wrapped SOL account", mint: WSOL_MINT, tokenProgram: TOKEN_PROGRAM }]);
  }

  private async assertAffordable(cost: CostEstimate): Promise<void> {
    if (!cost.enough) throw await this.insufficientGas(cost);
  }

  /**
   * The refusal for `cost`, pointing to this device's older reserve when the table can move it now (docs/18 section 6:
   * "the panel points to the move"). The older reserve is read only on a device that stores a legacy envelope. The
   * pointer is a hint: a failed read leaves it out, and the refusal stands as it is.
   */
  private async insufficientGas(cost: CostEstimate): Promise<InsufficientGas> {
    let movable: OlderReserve | null = null;
    try {
      const reading = await this.olderReserve();
      if (reading && reading.priced && (reading.plan.form === 'A' || reading.plan.form === 'B')) movable = reading;
    } catch {
      /* no pointer; the refusal below is unchanged */
    }
    return new InsufficientGas(cost, movable);
  }


  // ------------------------------------------------------------ writing

  /**
   * Activate (docs/08 5.1 step 6; docs/18 5.1 D, R79). Initialize is signed by the fee reserve's key and needs no XMSS
   * marker to be sent, so none is written before or during the send. The fresh read decides: a vault or a closed one is
   * left alone (nothing is sent, and no marker is created, renewed or deleted, so a legitimate existing marker is
   * preserved); a foreign account or a failed read refuses; `absent` or `prefunded` (a deposit that arrived first, which
   * the program adopts in place, R40) checks the live requirement (5.1 A) and sends. The carrier is compiled with a
   * fresh compute-unit draw (`activationLimits`), signed and simulated, and its signature is computed from its own wire
   * before `sendTransaction`; the engine holds that attempt and every outcome carries the local value, never the RPC's
   * reply. Then the wait for finalized success (`Rpc.confirmFinalized`, the strict `isFinalizedSuccess` predicate), a
   * finalized readback of an untouched tree 0 under this genesis key, and only then the tree-0 marker, if none exists.
   * Anything less (a failed carrier, a lost reply, a null or merely confirmed status by the deadline, a readback that
   * differs, a lock during any await) writes no marker: the device stays recovery-only and its next step is the key
   * refresh, as after Restore. Refusals before the send throw; outcomes after it are returned, including a readback that
   * failed after finality (`unknown`, the attempt still held for Check again). When the fresh read finds a vault while
   * the engine holds an attempt for it, that attempt may be what created it: it is checked, as Check again would, rather
   * than reported as `exists`.
   */
  async activate(): Promise<ActivationResult> {
    this.assertOpen();
    await this.assertDeployment();
    this.assertOpen();
    await this.assertTrustedProgram();
    this.assertOpen();
    const state = await this.readChain();
    this.assertOpen();
    if (state.kind === 'vault' || state.kind === 'tombstone') {
      const held = this.heldActivation();
      if (!held) return { step: 'exists', signature: null, detail: null };
      try {
        return await this.checkActivation();
      } catch (e) {
        if (this.closed) throw new Error('locked');
        return { step: 'unknown', signature: held.signature, detail: describe(e) };
      }
    }
    if (state.kind === 'foreign') throw new Error('The address holds an account that is not a vault of this program, so it cannot be activated.');
    const cost = await this.activationCost(state);
    this.assertOpen();
    if (!cost.enough) throw await this.insufficientGas(cost);
    const ix = initializeInstruction(this.programId, this.relayer.publicKey, this.vaultAddressBytes, this.info.bump, bytesOf(this.info.genesisKey));
    const bh = await this.rpc.getLatestBlockhash();
    this.assertOpen();
    const message = compileV1(this.relayer.publicKey, [ix], bh.blockhash, activationLimits(drawActivationR()));
    const wire = await signV1(message, this.relayer);
    const keys = message.accountKeys.map(toBase58);
    const sim = await this.rpc.simulateTransaction(wire);
    if (sim.err) throw withAccounts(new RelayerError('SimulationFailed', `simulation failed: ${JSON.stringify(sim.err)}`, { err: sim.err, logs: sim.logs, unitsConsumed: sim.unitsConsumed }), keys);
    this.assertOpen();
    const attempt: ActivationAttempt = { vaultAddress: this.info.address, signature: signatureOfWire(wire) };
    this.activation = attempt;
    try {
      await this.rpc.sendTransaction(wire);
    } catch (e) {
      // The wire may have reached the network: unknown, with the locally computed signature held for Check again.
      return { step: 'unknown', signature: attempt.signature, detail: describe(e) };
    }
    let landed: { slot: number; status: SignatureStatus };
    try {
      landed = await this.rpc.confirmFinalized(attempt.signature, ACTIVATION_FINALITY_MS, { abort: () => this.closed || this.activation !== attempt });
    } catch (e) {
      if (this.closed) throw new Error('locked');
      if (e instanceof RelayerError && e.code === 'ConfirmFailed') {
        this.dropActivation(attempt);
        return { step: 'failed', signature: attempt.signature, detail: describe(withAccounts(e, keys)) };
      }
      return { step: 'unknown', signature: attempt.signature, detail: describe(e) };
    }
    // The carrier is final: a readback or marker read that fails now leaves the outcome unknown, with the attempt held
    // so Check again can finish steps 4 and 5, never a refusal that hides it. Only a lock propagates.
    try {
      return await this.markActivated(attempt, landed.slot);
    } catch (e) {
      if (this.closed) throw new Error('locked');
      return { step: 'unknown', signature: attempt.signature, detail: describe(e) };
    }
  }

  /** The attempt the engine holds for this vault, if any (5.1 D step 3). */
  heldActivation(): ActivationAttempt | null {
    const a = this.activation;
    return a && a.vaultAddress === this.info.address ? { ...a } : null;
  }

  private dropActivation(attempt: ActivationAttempt): void {
    if (this.activation === attempt) this.activation = null;
  }

  /**
   * "Check again" for the held attempt (5.1 D step 6): one status read. A null entry is "not seen yet" and writes
   * nothing; an error is a failed carrier; anything short of the strict predicate (`isFinalizedSuccess`) is not final;
   * finalized success goes on to the readback and the marker, as the wait would have (`landed`, `not-visible` or
   * `differs`). With no attempt held (after a lock or a reload), there is nothing to check and the device stays
   * recovery-only. A read that fails throws, and the attempt stays held.
   */
  async checkActivation(): Promise<ActivationResult> {
    this.assertOpen();
    const attempt = this.activation;
    if (!attempt || attempt.vaultAddress !== this.info.address) return { step: 'none', signature: null, detail: null };
    const status = await this.rpc.signatureStatus(attempt.signature);
    this.assertOpen();
    if (status === null) return { step: 'not-seen', signature: attempt.signature, detail: null };
    if (status.err !== null && status.err !== undefined) {
      this.dropActivation(attempt);
      return { step: 'failed', signature: attempt.signature, detail: describe(new RelayerError('ConfirmFailed', `transaction ${attempt.signature} failed on chain: ${JSON.stringify(status.err)}`, status.err)) };
    }
    if (!isFinalizedSuccess(status)) return { step: 'not-final', signature: attempt.signature, detail: null };
    return this.markActivated(attempt, status.slot ?? (await this.rpc.getSlot()));
  }

  /**
   * After finalized success (5.1 D step 5): a finalized read of the vault must show tag vault, tree 0, a verifying key
   * equal to this device's genesis key, and no used leaf. Only then is the tree-0 `created` marker written, from the
   * landing slot, and only if none exists. A lock during either await writes nothing. No account, or one this program
   * does not own, is `not-visible` (the finalized read is behind the status that answered; Check again may complete it);
   * a vault account of this program that does not match is `differs`. Neither writes anything, and the attempt stays held.
   */
  private async markActivated(attempt: ActivationAttempt, landingSlot: number): Promise<ActivationResult> {
    this.assertOpen();
    const account = await this.rpc.getAccountInfo(this.info.address, 'finalized');
    this.assertOpen();
    if (!account || !equalBytes(account.owner, this.programId)) {
      return { step: 'not-visible', signature: attempt.signature, detail: null };
    }
    if (!this.untouchedTreeZero(account)) {
      return { step: 'differs', signature: attempt.signature, detail: 'The vault on chain is not the untouched tree 0 of this genesis key.' };
    }
    const existing = await this.store.getMarker(this.info.vaultId, 0);
    this.assertOpen();
    if (!existing) await VaultSigner.markTreeFresh(this.store, bytesOf(this.info.vaultId), 0, 'created', BigInt(landingSlot));
    this.dropActivation(attempt);
    return { step: 'landed', signature: attempt.signature, detail: null };
  }

  private untouchedTreeZero(account: OnChainAccount | null): boolean {
    if (!account) return false;
    try {
      const snap = snapshot(this.programId, account.owner, account.data);
      return snap.tag === TAG_VAULT && snap.treeIndex === 0 && equalBytes(snap.verifyingKey, bytesOf(this.info.genesisKey)) && snap.usedLeaves.every((b) => b === 0);
    } catch {
      return false;
    }
  }

  /**
   * Key refresh (docs/08 5.8): the tree's recovery message, leaf 255, signed
   * in the worker, sent by the gas tank; then a sync so the signer follows
   * the chain to tree t + 1 and writes that tree's marker. Allowed in every
   * mode, but never against an untrusted program (R42). The recovery record
   * is not burned on a failure: its message is deterministic and the same
   * refresh can be tried again. Once the engine is closed (the session's
   * lock) it refuses "locked" before each step that reads, simulates or
   * sends, so a refresh in flight at a lock stops at the next step and goes
   * no further than markSent's own refusal would let it; one already past
   * markSent finishes its confirmation (the outcome a page still shows).
   * Before `recover()` reserves leaf 255, the fee reserve must hold the
   * refresh's live requirement, F + 15,000 (docs/18 5.1 A, R78): below it the
   * refresh is refused with `InsufficientGas`, no leaf-255 record is written,
   * and an existing one is left exactly as it was.
   */
  async refreshKey(signer: VaultSigner, onProgress: (p: RefreshProgress) => void): Promise<void> {
    onProgress({ step: 'preparing' });
    let auth: Authorization;
    try {
      this.assertOpen();
      await this.assertDeployment();
      this.assertOpen();
      await this.assertTrustedProgram();
      this.assertOpen();
      const cost = await this.refreshCost();
      this.assertOpen();
      if (!cost.enough) throw await this.insufficientGas(cost);
      auth = await signer.recover();
      // A lock that landed while the worker signed (the signer's close waits behind this job): the signed record stays,
      // as any refusal before sending leaves it, and nothing more is read, simulated or sent, so the fee reserve's key
      // signs nothing after the lock.
      this.assertOpen();
    } catch (e) {
      onProgress({ step: 'failed', kind: 'rejected', error: describe(e) });
      throw new RefreshFailed('rejected', describe(e), e);
    }
    onProgress({ step: 'sending' });
    const ix: Instruction = { programId: this.programId, accounts: auth.accounts, data: auth.instructionData };
    let sent: { signature: string; transactionSlot: number };
    try {
      const bh = await this.rpc.getLatestBlockhash();
      this.clock.observe(Number(bh.slot));
      this.assertOpen();
      const wire = await this.simulateFor([ix], bh.blockhash, PLAIN_LIMITS, false);
      // the signer's markSent refuses after a lock too; this says so before the send step, in the same words
      this.assertOpen();
      sent = await this.sendWire(signer, auth.leaf, wire);
    } catch (e) {
      if (e instanceof Unknown) {
        const detail = describe(e.cause);
        onProgress({ step: 'unknown', kind: 'unknown', signature: e.signature, error: `${UNKNOWN_SENTENCE} ${detail}` });
        throw new RefreshFailed('unknown', detail, e.cause, e.signature);
      }
      if (e instanceof FailedOnChain) {
        const detail = describe(e.cause);
        onProgress({ step: 'failed', kind: 'failed', signature: e.signature, error: `The refresh landed and failed on chain: ${detail}` });
        throw new RefreshFailed('failed', detail, e.cause, e.signature);
      }
      const cause = e instanceof Rejected ? e.cause : e;
      const detail = describe(cause);
      onProgress({ step: 'failed', kind: 'rejected', error: `The refresh was not sent: ${detail}` });
      throw new RefreshFailed('rejected', detail, cause);
    }
    const readback = await this.settle(signer);
    onProgress({ step: 'landed', signature: sent.signature, ...(readback ? { error: readback } : {}) });
  }

  /**
   * "Make swappable" (docs/08 5.4, 01-architecture.md 7.6): wrap the vault's
   * entire stray native SOL into its wSOL ATA. One Execute carrying only
   * `LAMPORT_MOVE` to that ATA, followed in the same transaction by a
   * TOP-LEVEL `SyncNative` (an inner one is rejected on chain) - composed
   * exactly like the mainnet canary's step 3
   * (sdk/ts/scripts/mainnet-canary.ts). `purpose: 'transfer'`, the whole
   * `lamports` given. The shared pipeline applies: the same guards before
   * the key, the same four outcomes after it (R42, R44, R45, R49).
   */
  async wrapStray(signer: VaultSigner, lamports: bigint, onProgress: (p: ActionProgress) => void): Promise<ActionResult> {
    const wsolAta = await ataAddress(this.vaultAddressBytes, WSOL_MINT, TOKEN_PROGRAM);
    return this.runSigned(
      signer,
      async (slot) => ({
        header: wrapHeader(slot + 150n, lamports),
        aux: wrapAux(wsolAta),
        inners: [],
        top: [],
        compose: (execute) => wrapTransactionInstructions(this.programId, this.relayer.publicKey, this.vaultAddressBytes, wsolAta, { accounts: execute.accounts, instructionData: execute.data }),
        limits: TOKEN_TOUCHING_LIMITS,
        purpose: 'transfer',
        newAccounts: [{ address: wsolAta, label: "the vault's wrapped SOL account", mint: WSOL_MINT, tokenProgram: TOKEN_PROGRAM }],
      }),
      onProgress,
    );
  }

  /**
   * "Send out" from the gas tank (docs/08 5.7): a relayer-signed System
   * Transfer of `lamports` (or, with 'everything', the fresh balance less the
   * fee) to `destination`. This moves only the relayer's own SOL and never
   * touches a vault instruction or a one-time key. Before anything is signed,
   * a fresh read of the reserve and of the destination must pass the rule of
   * docs/18 5.1 A (M18): after the fee the reserve holds 0 or at least F, and
   * at the end 0 or at least F; a destination below F must end at F or more.
   * A refusal throws `SendOutRefused`. A chain error names the account at its
   * index (5.1 B).
   */
  async sendGas(destination: Uint8Array, lamports: bigint | 'everything'): Promise<SendResult> {
    this.assertOpen();
    // docs/18 2.5: before the reads and the signature; a lock during this read leaves nothing signed.
    await this.assertDeployment();
    this.assertOpen();
    const rule = await this.sendOutRule();
    const target = await this.rpc.getAccountInfo(toBase58(destination));
    this.assertOpen();
    const destinationLamports = target?.lamports ?? 0n;
    const amount = lamports === 'everything' ? rule.balance - rule.fee : lamports;
    const check = sendOutCheck({ balance: rule.balance, minimum: rule.minimum, amount, fee: rule.fee, destinationLamports });
    if (!check.ok) throw new SendOutRefused(check.reason, { minimum: rule.minimum, everything: check.everything, maxPartial: check.maxPartial }, sendOutText(check.reason, { ...rule, maxPartial: check.maxPartial, destinationLamports }));
    const ix = systemTransferInstruction(this.relayer.publicKey, destination, amount);
    const bh = await this.rpc.getLatestBlockhash();
    this.assertOpen();
    const message = compileV1(this.relayer.publicKey, [ix], bh.blockhash, PLAIN_LIMITS);
    const wire = await signV1(message, this.relayer);
    const keys = message.accountKeys.map(toBase58);
    const sim = await this.rpc.simulateTransaction(wire);
    if (sim.err) throw withAccounts(new RelayerError('SimulationFailed', `simulation failed: ${JSON.stringify(sim.err)}`, { err: sim.err, logs: sim.logs, unitsConsumed: sim.unitsConsumed }), keys);
    this.assertOpen();
    const signature = signatureOfWire(wire);
    await this.rpc.sendTransaction(wire);
    let transactionSlot: number;
    try {
      transactionSlot = await this.rpc.confirm(signature);
    } catch (e) {
      throw e instanceof RelayerError ? withAccounts(e, keys) : e;
    }
    const observedSlot = await this.rpc.getSlot();
    return { signature, wire, transactionSlot, observedSlot, unitsConsumed: sim.unitsConsumed };
  }

  // ------------------------------------------------------ older reserves

  /**
   * This device's older fee reserve (docs/18 section 6), or null when it stores no legacy envelope. The envelope is
   * not opened: the balance is read by the recorded address, so Home and Settings show the notice without a
   * passphrase. Reads, in order: the network check (2.5, before any address is shown as a destination), the guard's
   * records (`currentRecords`), F, the older account (skipped while a pre-R48 address is unknown: null, 0 lamports),
   * and the derived reserve's account, so both balances are the last reads. Read afresh at every call. A failed read
   * of the envelope throws `EnvelopeUnreadable` before the network check. While a sweep awaits its outcome the
   * reading is not priced and `pending`, with that as its reason, so no second move is offered; a sweep an earlier
   * runtime sent is watched from here on (`resumeSweepWatch`).
   */
  async olderReserve(): Promise<OlderReserve | null> {
    this.assertOpen();
    const sealed = await this.readLegacyEnvelope();
    this.assertOpen();
    if (!sealed) return null;
    const reading = await this.readOlderReserve();
    // After the network check, never at construction: unlock makes no network read (docs/18 section 3).
    this.resumeSweepWatch();
    // Judged after the last read: a sweep whose outcome became known meanwhile no longer holds the reading back. An
    // account the move would not price keeps its own reason; otherwise the pending sweep is the only one.
    if (!this.sweepPending() || !reading.priced) return reading;
    return { ...reading, priced: false, unpricedReason: SWEEP_PENDING_REASON, pending: true };
  }

  /** A sweep of this engine runs, or the session's latch holds a sent one with no known outcome (docs/18 section 6). */
  private sweepPending(): boolean {
    return this.sweeping || this.pendingSweepOf() !== null;
  }

  /**
   * Open this device's older reserve key into the worker's sweep-only set (docs/18 2.4 `openLegacy`, section 6): a
   * wrong passphrase or a damaged envelope rejects here, and nothing is sent. With `VaultInfo.relayer` recorded the
   * opened key must equal it; on a pre-R48 device its address is recorded through the session's compare-and-set
   * writer, the only write to the device setup on this path, after the lifetime check, so a locked or forgotten
   * session changes no setup. The key stays in the worker, able to sign only a sweep into the derived reserve, until
   * lock. Returns its base58 address.
   */
  async openOlderReserve(passphrase: string): Promise<string> {
    return toBase58(await this.openLegacyKey(passphrase));
  }

  /** The stored legacy envelope, or undefined; a failed read is `EnvelopeUnreadable`, unless a lock came first ("locked"). */
  private async readLegacyEnvelope(): Promise<SealedEnvelope | undefined> {
    try {
      return await this.legacyEnvelopeOf();
    } catch (e) {
      // A Forget in another tab closes this tab's database and locks it (docs/18 section 6): that reads as the lock.
      this.assertOpen();
      throw new EnvelopeUnreadable(e);
    }
  }

  private async openLegacyKey(passphrase: string): Promise<Uint8Array> {
    this.assertOpen();
    const sealed = await this.readLegacyEnvelope();
    this.assertOpen();
    if (!sealed) throw new SweepRefused('none', null, 'This device holds no older fee reserve key. Nothing was sent.');
    const key = await this.client.openLegacy(sealed, passphrase);
    this.assertOpen();
    const opened = toBase58(key);
    const recorded = this.legacyAddress;
    if (recorded !== null) {
      if (opened !== recorded) throw new SweepRefused('mismatch', null, `The older fee reserve key on this device opens to ${opened}, not the recorded address ${recorded}, so it is not used. Nothing was sent.`);
      return key;
    }
    await this.recordLegacyAddressOf(opened);
    this.assertOpen();
    this.legacyAddress = opened;
    return key;
  }

  /**
   * Move the older reserve into the derived one (docs/18 section 6), after the review and the passphrase prompt. The
   * key is opened (`openOlderReserve`), and then the recheck runs after that last wait: the network check, the guard,
   * F and both balances are read again and the form recomputed. A refusal (not priced, blocked, not yet, nothing)
   * throws `SweepRefused`; a form, amount or fee other than `expected` throws `SweepChanged` and the page shows the
   * review again. Any other stop before the first send attempt (the passphrase, a read, the worker, the simulation, a
   * lock) throws `SweepNotSent` around its cause. Nothing is sent in any of those cases. The amount is the plan's
   * (Form A: L − 15,000; Form B: all of L), never the caller's. The worker compiles and signs the transfer
   * (`sweep`); `sendSignedV1` simulates, sends and confirms it, with `assertOpen` as its checkpoint after the
   * simulation and before every send attempt. `assertOpen` also runs after every await before that, the worker's
   * `sweep` included, so a lock at any point up to the send drops the wire unsent (no send after Lock); a lock after
   * the send lets the confirmation finish, and its outcome goes to `onSweepOutcome` before this returns or throws
   * it as it is; an error thrown after the first send attempt carries the signature (`sentSweepSignature`). One sweep
   * at a time: while this engine's runs (`sweeping`) or the session's latch holds a sent one with no known outcome
   * (`pendingSweepOf`), this refuses ('pending').
   */
  async sweep(passphrase: string, expected: { form: 'A' | 'B'; amount: bigint; fee: bigint }): Promise<SendResult> {
    // A closed engine sends nothing and says so as every stop before the send does; a lock reads "locked" first.
    if (this.closed) throw new SweepNotSent(new Error('locked'));
    if (this.sweepPending()) {
      // a sweep an earlier runtime sent is watched from here on too, so the refusal ends on its own
      this.resumeSweepWatch();
      throw new SweepRefused('pending', null, SWEEP_PENDING_REFUSAL);
    }
    this.sweeping = true;
    try {
      return await this.sweepOnce(passphrase, expected);
    } finally {
      this.sweeping = false;
    }
  }

  /**
   * One sweep, under `sweeping`. The session's latch is set at the first send attempt, synchronously after that
   * attempt's checkpoint has passed and before its request is made, and cleared once the outcome is known: landed,
   * failed on chain, or, when the confirmation gave up or the send never answered, when `watchSweep` sees the signature
   * land or fail or its blockhash expire. A lock that ends the watch leaves the latch set for the next runtime.
   */
  private async sweepOnce(passphrase: string, expected: { form: 'A' | 'B'; amount: bigint; fee: bigint }): Promise<SendResult> {
    const prepared = await this.prepareSweep(passphrase, expected).catch((e: unknown) => {
      throw e instanceof SweepRefused || e instanceof SweepChanged ? e : new SweepNotSent(e);
    });
    const { plan, wire, lastValidBlockHeight } = prepared;
    const signature = payerSignatureOfWire(wire);
    // One-way, set at the first send attempt once its checkpoint has passed: a stop before it sent nothing.
    const latch = { sent: false };
    try {
      const result = await sendSignedV1(this.rpc, wire, {
        checkpoint: (stage) => {
          this.assertOpen();
          if (stage === 'attempt' && !latch.sent) {
            latch.sent = true;
            this.setPendingSweepOf({ signature, lastValidBlockHeight, form: plan.form, amount: plan.amount });
          }
        },
      });
      this.clearPendingSweep(signature);
      this.reportSweep({ signature: result.signature, amount: plan.amount, form: plan.form, landed: true, failed: false, detail: null });
      return result;
    } catch (e) {
      // A chain error names the account at its index (5.1 B): Form A's payer, index 0, is the older key; Form B's is the derived reserve.
      const err = e instanceof RelayerError ? withAccounts(e, accountKeysOfWire(wire)) : e;
      if (!latch.sent) throw new SweepNotSent(err);
      // Failed on chain is a known outcome. A confirmation that gave up, or a send that never answered, may still
      // land: the latch holds until the watch knows.
      const failed = err instanceof RelayerError && err.code === 'ConfirmFailed';
      if (failed) this.clearPendingSweep(signature);
      else this.watchPendingSweep(signature, lastValidBlockHeight);
      if (typeof err === 'object' && err !== null) sweepSignatureOf.set(err, signature);
      this.reportSweep({ signature, amount: plan.amount, form: plan.form, landed: false, failed, detail: sweepOutcomeDetail(err, plan.form) });
      throw err;
    }
  }

  /** Clear the session's latch, only if it still holds this signature: a later sweep's latch is never cleared by an earlier one's outcome. */
  private clearPendingSweep(signature: string): void {
    if (this.pendingSweepOf()?.signature === signature) this.setPendingSweepOf(null);
  }

  /** A sweep an earlier runtime sent, still in the session's latch: this engine watches it (one watch per signature). */
  private resumeSweepWatch(): void {
    const pending = this.pendingSweepOf();
    if (pending) this.watchPendingSweep(pending.signature, pending.lastValidBlockHeight);
  }

  /** Run `watchSweep` once per signature in this engine; its known outcome clears the latch, a lock that ends it does not. */
  private watchPendingSweep(signature: string, lastValidBlockHeight: bigint): void {
    if (this.closed || this.watching === signature) return;
    this.watching = signature;
    const done = (known: boolean): void => {
      if (this.watching === signature) this.watching = null;
      if (known) this.clearPendingSweep(signature);
    };
    void this.watchSweep(signature, lastValidBlockHeight).then(done, () => done(false));
  }

  /** The sweep up to its signed wire (docs/18 section 6): the opening, the recheck after that last wait, and the worker's signature; `assertOpen` after every await. */
  private async prepareSweep(passphrase: string, expected: { form: 'A' | 'B'; amount: bigint; fee: bigint }): Promise<{ plan: Extract<SweepPlan, { form: 'A' | 'B' }>; wire: Uint8Array; lastValidBlockHeight: bigint }> {
    const source = await this.openLegacyKey(passphrase);
    this.assertOpen();
    const reading = await this.readOlderReserve();
    this.assertOpen();
    const plan = this.movablePlan(reading);
    // Defensive: the reading must be of the key just opened (it always is: the address was compared or recorded above).
    if (reading.address !== toBase58(source)) throw new SweepChanged(reading);
    if (plan.form !== expected.form || plan.amount !== expected.amount || plan.fee !== expected.fee) throw new SweepChanged(reading);
    const bh = await this.rpc.getLatestBlockhash();
    this.assertOpen();
    const wire = await this.client.sweep({ source, lamports: plan.amount, blockhash: bh.blockhash, payer: plan.payer });
    // A lock while the worker signed: the wire is dropped here, unsent.
    this.assertOpen();
    return { plan, wire, lastValidBlockHeight: bh.lastValidBlockHeight };
  }

  /** Hand a sent sweep's outcome to the session (`EngineDeps.onSweepOutcome`); a throw there never changes the sweep's own result. */
  private reportSweep(outcome: SweepOutcome): void {
    try {
      this.onSweepOutcomeOf?.(outcome);
    } catch {
      /* the sweep returns or throws as it would without the callback */
    }
  }

  /**
   * Watch a sweep sent with no known outcome (docs/18 section 6): its confirmation gave up, or its send never answered,
   * so it may still land. Polls the wire's own signature until it lands or fails on chain, or until its blockhash has
   * expired with no status, after which it can never land. Expiry is judged from the RPC's own figures: the latest
   * blockhash's `lastValidBlockHeight` is the current block height plus `BLOCKHASH_LIFETIME`, as the sweep's was, so
   * the sweep's has expired once the latest one's exceeds it by more than that. A failed read is retried at the next
   * poll. A lock ends the watch: the engine is dropped then, with its state, and no read starts after it. Resolves
   * true once the outcome is known, false when a lock ended the watch first (the session's latch then stays set, and
   * the next runtime's engine resumes the watch).
   */
  private async watchSweep(signature: string, lastValidBlockHeight: bigint): Promise<boolean> {
    let expired = false;
    for (;;) {
      if (this.closed) return false;
      try {
        const status = await this.rpc.signatureStatus(signature);
        if (status && ((status.err !== null && status.err !== undefined) || status.confirmationStatus === 'confirmed' || status.confirmationStatus === 'finalized')) return true;
        // One status read after the expiry was seen, so a landing in the last valid block is not missed.
        if (expired && !status) return true;
        if (this.closed) return false;
        const latest = await this.rpc.getLatestBlockhash();
        expired = latest.lastValidBlockHeight > lastValidBlockHeight + BLOCKHASH_LIFETIME;
      } catch {
        /* retried at the next poll */
      }
      await new Promise((resolve) => setTimeout(resolve, SWEEP_WATCH_MS));
    }
  }

  /** The plan of a reading the sweep may send, or the refusal (docs/18 section 6): an unpriced account refuses first, then the table's own answers. */
  private movablePlan(reading: OlderReserve): Extract<SweepPlan, { form: 'A' | 'B' }> {
    if (!reading.priced) throw new SweepRefused('not-priced', reading, reading.unpricedReason ?? 'An account the move would touch is not an empty System account. Nothing was sent.');
    const plan = reading.plan;
    if (plan.form === 'A' || plan.form === 'B') return plan;
    throw new SweepRefused(plan.form, reading, sweepRefusalText(plan.form, plan));
  }

  /** One reading of the older reserve, in the order `olderReserve` documents; `assertOpen` after every await. */
  private async readOlderReserve(): Promise<OlderReserve> {
    await this.assertDeployment();
    this.assertOpen();
    const records = await this.currentRecords();
    this.assertOpen();
    const minimum = await this.reserveMinimum();
    this.assertOpen();
    const olderAddress = this.legacyAddress;
    const older = olderAddress === null ? null : await this.rpc.getAccountInfo(olderAddress);
    this.assertOpen();
    const current = await this.rpc.getAccountInfo(this.relayerAddress);
    this.assertOpen();
    const lamports = older?.lamports ?? 0n;
    const currentLamports = current?.lamports ?? 0n;
    // A recorded older address equal to the derived one (a tampered row) would price a move of the reserve into itself.
    const unpricedReason =
      olderAddress === this.relayerAddress
        ? 'The older fee reserve address recorded on this device is your fee reserve itself, so there is nothing to move. Nothing is sent.'
        : (unpricedText(older, "The older fee reserve's", 'of') ?? unpricedText(current, "Your fee reserve's", 'into'));
    return {
      address: olderAddress,
      lamports,
      current: currentLamports,
      minimum,
      plan: sweepPlan({ older: lamports, current: currentLamports, minimum, records }),
      priced: unpricedReason === null,
      unpricedReason,
      // a pending sweep is `olderReserve`'s to judge; the recheck refuses it before reading (`sweep`)
      pending: false,
    };
  }

  /**
   * The guard's input (docs/18 section 6): the current tree's unresolved records, recovery records included (the
   * plan skips those). Empty only when the chain holds no vault of this program (absent, prefunded or foreign), where
   * no signer exists and the guard is vacuous. Otherwise the signer is opened if no page has opened it yet (its open
   * reconciles the ledger, R69), so the guard never depends on which page asked. It runs no sync of its own: a sync
   * beside the page's would read the chain again over one signer, which R69 P8 (R77) forbids. The trade-off: the guard
   * reads the ledger's unresolved records as the page last synced them (Home syncs on load); `pending()` reads the
   * store, so a record another tab wrote since is seen, and a record that landed or expired since the last sync keeps
   * blocking until the page's next sync, which is the conservative side. A failure (a read, a key mismatch) rejects:
   * an unread guard never passes.
   */
  private async currentRecords(): Promise<readonly LeafRecord[]> {
    const state = await this.readChain();
    this.assertOpen();
    if (state.kind !== 'vault' && state.kind !== 'tombstone') return [];
    const signer = await this.signerFor(state);
    if (this.closed || signer.isClosing) throw new Error('locked');
    return signer.pending();
  }

  // ------------------------------------------------------- the pipeline

  /** Guard, reserve a key, sign, simulate (twice when tightening), send, confirm, settle: the one pipeline of every signed action (R11, R41-R46, R49). */
  private async runSigned(signer: VaultSigner, build: (slot: bigint) => Promise<Built>, onProgress: (p: ActionProgress) => void, opts: { tighten?: boolean; text?: (cause: unknown) => string } = {}): Promise<ActionResult> {
    await this.guard(signer);
    onProgress({ step: 'signing' });
    const bh = await this.rpc.getLatestBlockhash();
    this.clock.observe(Number(bh.slot));
    const built = await build(bh.slot);
    await this.assertAffordable(await this.estimateCost(built.limits, built.newAccounts));
    let auth: Authorization;
    try {
      auth = await signer.authorize({ header: built.header, aux: built.aux, inners: built.inners, purpose: built.purpose, currentSlot: bh.slot });
    } catch (e) {
      // Nothing was reserved: the signer refused (an unresolved record, a stale slot). No key to retire.
      onProgress({ step: 'rejected', error: describe(e) });
      throw e;
    }
    // Every signed field comes from the authorization itself, the automatic successor included (R43).
    const execute: Instruction = { programId: this.programId, accounts: auth.accounts, data: auth.instructionData };
    const instructions = built.compose ? built.compose(execute) : [...built.top, execute, ...(built.after ?? [])];
    let sent: { signature: string; transactionSlot: number };
    try {
      onProgress({ step: 'checking', leaf: auth.leaf, validUntilSlot: built.header.validUntilSlot });
      const wire = await this.simulateFor(instructions, bh.blockhash, built.limits, opts.tighten === true);
      onProgress({ step: 'sending', leaf: auth.leaf, signature: signatureOfWire(wire), validUntilSlot: built.header.validUntilSlot });
      sent = await this.sendWire(signer, auth.leaf, wire);
    } catch (e) {
      return this.outcome(signer, auth.leaf, e, onProgress, opts.text ?? describe);
    }
    const readback = await this.settle(signer);
    onProgress({ step: 'landed', leaf: auth.leaf, signature: sent.signature, ...(readback ? { note: readback } : {}) });
    return { signature: sent.signature, leaf: auth.leaf, transactionSlot: sent.transactionSlot, readback };
  }

  /** Simulate with the given limits, optionally tighten the compute limit from the first run and simulate again (docs/01 7.1 step 6). Returns the signed wire; sends nothing. */
  private async simulateFor(instructions: Instruction[], blockhash: Uint8Array, limits: Limits, tighten: boolean): Promise<Uint8Array> {
    let message = compileV1(this.relayer.publicKey, instructions, blockhash, limits);
    let wire = await signV1(message, this.relayer);
    const sim = await this.rpc.simulateTransaction(wire);
    // The message's keys travel with the error, so a transaction-level error can name the account at its index (5.1 B).
    if (sim.err) throw withAccounts(new RelayerError('SimulationFailed', `simulation failed: ${JSON.stringify(sim.err)}`, { err: sim.err, logs: sim.logs, unitsConsumed: sim.unitsConsumed }), message.accountKeys.map(toBase58));
    if (tighten && sim.unitsConsumed !== undefined) {
      const tightened: Limits = { ...limits, computeUnitLimit: Math.min(Math.ceil((sim.unitsConsumed * 12) / 10), 400_000) };
      message = compileV1(this.relayer.publicKey, instructions, blockhash, tightened);
      wire = await signV1(message, this.relayer);
      const again = await this.rpc.simulateTransaction(wire);
      if (again.err) throw withAccounts(new RelayerError('SimulationFailed', `simulation failed: ${JSON.stringify(again.err)}`, { err: again.err, logs: again.logs, unitsConsumed: again.unitsConsumed }), message.accountKeys.map(toBase58));
    }
    return wire;
  }

  /**
   * Record the send under the transaction's own signature, then send and
   * confirm (R45). The record says "sent" before the wire leaves, so a lost
   * reply can only leave an unknown record, never an unrecorded send. A send
   * error is unknown (the wire may have reached the network); a confirmed
   * failure is `FailedOnChain`; a confirmation timeout is unknown.
   */
  private async sendWire(signer: VaultSigner, leaf: number, wire: Uint8Array): Promise<{ signature: string; transactionSlot: number }> {
    const signature = signatureOfWire(wire);
    await signer.markSent(leaf, signature);
    try {
      await this.rpc.sendTransaction(wire);
    } catch (e) {
      throw new Unknown(signature, e);
    }
    try {
      return { signature, transactionSlot: await this.rpc.confirm(signature) };
    } catch (e) {
      if (e instanceof RelayerError && e.code === 'ConfirmFailed') throw new FailedOnChain(signature, withAccounts(e, accountKeysOfWire(wire)));
      throw new Unknown(signature, e);
    }
  }

  /** After a confirmed landing: the slot observation and the sync that lets the signer follow the chain. A failure here is a note on a landed action, never a failed action (R45). */
  private async settle(signer: VaultSigner): Promise<string | null> {
    try {
      const observed = await this.observeSlot();
      const account = await this.rpc.getAccountInfo(this.info.address);
      if (account) await signer.sync(account.data, BigInt(observed));
      return null;
    } catch (e) {
      return `It landed, but the vault could not be read back afterwards: ${describe(e)} The ledger catches up at the next read.`;
    }
  }

  /** Turn a pipeline failure into the user's outcome (R45): a rejected or failed attempt retires its key; an unknown one stays "sent" for `reconcile`. */
  private async outcome(signer: VaultSigner, leaf: number, e: unknown, onProgress: (p: ActionProgress) => void, text: (cause: unknown) => string): Promise<never> {
    if (e instanceof FailedOnChain) {
      const detail = text(e.cause);
      await this.burn(signer, leaf, `failed on chain: ${detail}`);
      onProgress({ step: 'failed', leaf, signature: e.signature, error: `${FAILED_SENTENCE} ${detail}` });
      throw new ActionFailed('failed', detail, e.cause, e.signature);
    }
    if (e instanceof Unknown) {
      const detail = text(e.cause);
      onProgress({ step: 'unknown', leaf, signature: e.signature, error: `${UNKNOWN_SENTENCE} ${detail}` });
      throw new ActionUnknown(e.signature, detail, e.cause);
    }
    const cause = e instanceof Rejected ? e.cause : e;
    const detail = text(cause);
    await this.burn(signer, leaf, `rejected before sending: ${detail}`);
    onProgress({ step: 'rejected', leaf, error: `${REJECTED_SENTENCE} ${detail}` });
    throw new ActionFailed('rejected', detail, cause);
  }

  private async burn(signer: VaultSigner, leaf: number, note: string): Promise<void> {
    try {
      await signer.markBurned(leaf, note);
    } catch {
      /* the failure itself is what matters; a burn-record failure does not change it */
    }
  }

  // --------------------------------------------------------------- withdraw

  /** Native SOL out (docs/08 5.6): one Execute with `LAMPORT_MOVE` to the destination; the program keeps the vault's rent floor. */
  async withdrawNative(signer: VaultSigner, destination: Uint8Array, lamports: bigint, onProgress: (p: ActionProgress) => void): Promise<ActionResult> {
    return this.runSigned(
      signer,
      async (slot) => ({
        header: { numAux: 1, validUntilSlot: slot + 150n, lamportMove: { auxIndex: 0, lamports } },
        aux: [{ pubkey: destination, isSigner: false, isWritable: true }],
        inners: [],
        top: [],
        limits: PLAIN_LIMITS,
        purpose: 'transfer',
        newAccounts: [],
      }),
      onProgress,
    );
  }

  /** A token out (docs/08 5.6), wSOL included: the destination's associated account is created by the gas tank, then an inner `TransferChecked` signed by the vault. */
  async withdrawToken(signer: VaultSigner, destinationOwner: Uint8Array, mint: Uint8Array, tokenProgram: Uint8Array, amount: bigint, decimals: number, onProgress: (p: ActionProgress) => void): Promise<ActionResult> {
    const vaultAta = await ataAddress(this.vaultAddressBytes, mint, tokenProgram);
    const destAta = await ataAddress(destinationOwner, mint, tokenProgram);
    const data = concat(u8(12), u64le(amount), u8(decimals));
    return this.runSigned(
      signer,
      async (slot) => ({
        header: { numAux: 0, validUntilSlot: slot + 150n },
        aux: [],
        inners: [{ program: tokenProgram, metas: [{ pubkey: vaultAta, isWritable: true }, { pubkey: mint, isWritable: false }, { pubkey: destAta, isWritable: true }, { pubkey: this.vaultAddressBytes, isWritable: false }], data }],
        top: [createAtaIdempotent(this.relayer.publicKey, destinationOwner, destAta, mint, tokenProgram)],
        limits: TOKEN_TOUCHING_LIMITS,
        purpose: 'transfer',
        newAccounts: [{ address: destAta, label: `the destination's ${symbolOf(toBase58(mint))} account`, mint, tokenProgram }],
      }),
      onProgress,
    );
  }

  /** Unwrap (docs/08 5.6, docs/01 7.6): an inner `CloseAccount` of the vault's wSOL account with the vault as destination, so the SOL becomes native again. Its own action. */
  async unwrap(signer: VaultSigner, onProgress: (p: ActionProgress) => void): Promise<ActionResult> {
    const wsolAta = await ataAddress(this.vaultAddressBytes, WSOL_MINT, TOKEN_PROGRAM);
    return this.runSigned(
      signer,
      async (slot) => ({
        header: { numAux: 0, validUntilSlot: slot + 150n },
        aux: [],
        inners: [{ program: TOKEN_PROGRAM, metas: [{ pubkey: wsolAta, isWritable: true }, { pubkey: this.vaultAddressBytes, isWritable: true }, { pubkey: this.vaultAddressBytes, isWritable: false }], data: u8(9) }],
        top: [],
        limits: TOKEN_TOUCHING_LIMITS,
        purpose: 'transfer',
        newAccounts: [],
      }),
      onProgress,
    );
  }

  // ------------------------------------------------------------------- swap

  private jupiter(): JupiterClient {
    const key = this.jupiterKeyOf();
    return new JupiterClient({ baseUrl: `${this.settingsOf().jupiterUrl.replace(/\/$/, '')}/swap/v1`, ...(key ? { apiKey: key } : {}) });
  }

  async tokenProgramOf(mintB58: string): Promise<Uint8Array> {
    const account = await this.rpc.getAccountInfo(mintB58);
    if (!account) throw new Error(`The mint ${mintB58} does not exist on this cluster.`);
    return account.owner;
  }

  async decimalsOf(mintB58: string): Promise<number> {
    const cached = this.decimalsCache.get(mintB58);
    if (cached !== undefined) return cached;
    const d = await this.rpc.getMintDecimals(mintB58);
    this.decimalsCache.set(mintB58, d);
    return d;
  }

  /** The vault's balance in `tokenAccount`, or zero when the account does not exist yet. */
  async tokenAmount(tokenAccount: Uint8Array): Promise<bigint> {
    const account = await this.rpc.getAccountInfo(toBase58(tokenAccount));
    if (!account || account.data.length < 72) return 0n;
    return new DataView(account.data.buffer, account.data.byteOffset, account.data.byteLength).getBigUint64(64, true);
  }

  /**
   * Quote (docs/08 5.5 steps 1 and 2): the baseline quote fixes what the
   * signature will bind, then the maxAccounts ladder finds a route the
   * vault can carry. Nothing here reserves or signs. The route's own
   * arguments must promise what the quote promised, or the router is
   * lying and nothing is signed. With a `signer` whose rotation is due, the
   * header carries the successor already, so the ladder sizes the
   * transaction that will actually be sent (R43).
   *
   * The treasury fee's side follows from the two mints (`feeMint`,
   * program/src/fee.rs): on the output side, as before, `maxIn` is the
   * amount and the fee comes out of the gross output; on the input side the
   * requested amount is what the route spends, `maxIn` is that amount plus
   * the fee on it, the fee lands in the treasury's account for the input
   * mint, and the output minimum is what the vault keeps.
   */
  async quoteSwap(request: SwapRequest, signer?: VaultSigner): Promise<SwapPlan> {
    const settings = this.settingsOf();
    const client = this.jupiter();
    const inMint = addressBytes(address(request.inMint));
    const outMint = addressBytes(address(request.outMint));
    const [inTokenProgram, outTokenProgram, inDecimals, outDecimals] = await Promise.all([this.tokenProgramOf(request.inMint), this.tokenProgramOf(request.outMint), this.decimalsOf(request.inMint), this.decimalsOf(request.outMint)]);
    const inAta = await ataAddress(this.vaultAddressBytes, inMint, inTokenProgram);
    const outAta = await ataAddress(this.vaultAddressBytes, outMint, outTokenProgram);
    const mints: TokenPrograms = { inMint, inTokenProgram, outMint, outTokenProgram };
    const paying = feeMint(mints);
    const feeAta = await feeAccount(this.programId, paying.mint, paying.tokenProgram);
    const expect: Expected = { vault: this.vaultAddressBytes, inAta, outAta, inMint, outMint, otherVaultTokenAccounts: [], feeAccount: feeAta };
    const maxIn = maxInForRoute(paying.side, request.amount);
    if (maxIn === null) throw new Error('That amount and the treasury fee on it are more than a token account can hold. Nothing was signed.');
    const excluded = new Set([...settings.excludedVenues, ...this.sessionExclusions]);
    // ST6a (docs/19 section 3.4): LST_VENUES for wrapped SOL and one listed LST, PUBLIC_DEXES for every other pair.
    const venues = swapVenues(request.inMint, request.outMint);
    if (!venues) throw new Error('Swapping one liquid staking token for another is not offered. Swap to SOL first. Nothing was signed.');
    const dexes = venues.dexes.filter((d) => !excluded.has(d));
    const quoteRequest = { inputMint: request.inMint, outputMint: request.outMint, amount: request.amount, slippageBps: request.slippageBps, dexes, restrictIntermediateTokens: true };
    const baseline = await client.quote(quoteRequest);
    const quotedOut = BigInt(baseline.outAmount);
    const grossMin = jupiterMinOut(quotedOut, request.slippageBps);
    const nextKey = signer?.rotationDue ? await signer.successor() : undefined;
    const slot = BigInt(await this.observeSlot());
    const ladder = await quoteWithinLimits({
      client,
      request: quoteRequest,
      user: this.info.address,
      expect,
      params: { relayer: this.relayer.publicKey, validUntilSlot: slot + 150n, maxIn, minOut: grossMin, bounded: request.bounded, ...(nextKey ? { nextKey } : {}) },
      mints,
      programId: this.programId,
      vault: this.vaultAddressBytes,
      ...(venues.lst ? { useSharedAccounts: lstSharedAccounts } : {}),
    });
    if (ladder.route.args.quotedOutAmount !== BigInt(ladder.quote.outAmount) || ladder.route.args.inAmount !== BigInt(ladder.quote.inAmount)) {
      throw new Error("The router's route promises a different amount than its quote. Nothing was signed.");
    }
    const fee = paying.side === 'input' ? protocolFee(request.amount) : protocolFee(grossMin);
    return {
      request,
      ladder,
      expect,
      mints,
      feeSide: paying.side,
      feeMint: toBase58(paying.mint),
      feeDecimals: paying.side === 'input' ? inDecimals : outDecimals,
      maxIn,
      quotedOut,
      grossMin,
      fee,
      netMin: paying.side === 'input' ? grossMin : grossMin - fee,
      routeLabels: ladder.quote.routePlan.map((r) => r.swapInfo.label ?? r.swapInfo.ammKey),
      inDecimals,
      outDecimals,
      feeAccount: toBase58(feeAta),
      quotedAt: Date.now(),
      skipped: ladder.attempts,
      ...(nextKey ? { nextKey } : {}),
      rotates: !!nextKey,
    };
  }

  /**
   * Confirm (docs/08 5.5 steps 3 and 4): signs exactly the reviewed plan
   * (R41). A plan older than `QUOTE_MAX_AGE_MS` is refused before any key,
   * with `QuoteStale`, so the caller fetches a fresh quote, shows it and
   * asks again; nothing is ever re-quoted and signed in one breath. Then:
   * the guards, reserve and sign, build the four-instruction body around
   * the signer's own Execute (R43), simulate with ceiling limits, tighten,
   * simulate again, send, confirm, settle, and read the receipt from the
   * landed transaction itself (R46). In BOUNDED mode, and only for a route
   * failure in simulation, the identical signature is re-sent with a fresh
   * route until the expiry.
   */
  async confirmSwap(signer: VaultSigner, plan: SwapPlan, onProgress: (p: ActionProgress) => void): Promise<SwapResult> {
    await this.guard(signer);
    if (Date.now() - plan.quotedAt > QUOTE_MAX_AGE_MS) throw new QuoteStale();
    if (plan.feeSide === 'input') {
      // The fee is taken from the input account after the swap; an account that cannot cover the amount and the fee
      // would fail the fee transfer in the pre-flight check and retire a key, so it is refused here, before any key.
      // The token amount is read, not the lamports: a route can only spend what the amount field holds.
      const held = await this.tokenAmount(plan.expect.inAta);
      if (held < plan.maxIn) throw new InsufficientInput(held, plan.maxIn);
    }
    await this.assertAffordable(await this.swapCost(plan));
    onProgress({ step: 'signing' });
    const bh = await this.rpc.getLatestBlockhash();
    this.clock.observe(Number(bh.slot));
    const params: SwapParams = { relayer: this.relayer.publicKey, validUntilSlot: bh.slot + 150n, maxIn: plan.maxIn, minOut: plan.grossMin, bounded: plan.request.bounded, ...(plan.nextKey ? { nextKey: plan.nextKey } : {}) };
    const assembly = assembleExecute(plan.ladder.route, plan.expect, params);
    let auth: Authorization;
    try {
      auth = await signer.authorize({ header: assembly.header, aux: assembly.aux, inners: assembly.inners, purpose: plan.request.bounded ? 'swap-bounded' : 'swap-exact', currentSlot: bh.slot });
    } catch (e) {
      onProgress({ step: 'rejected', error: describe(e) });
      throw e;
    }
    let sent: { signature: string; transactionSlot: number };
    try {
      onProgress({ step: 'checking', leaf: auth.leaf, validUntilSlot: params.validUntilSlot });
      // The Execute that goes out is the one the signer signed: same header (successor included), same inner instructions (R43).
      let execute: Instruction = { programId: this.programId, accounts: auth.accounts, data: auth.instructionData };
      let instructions = await transactionInstructionsAround(execute, this.programId, this.vaultAddressBytes, this.relayer.publicKey, plan.mints);
      let wire: Uint8Array | null = null;
      for (let attempt = 0; wire === null; attempt++) {
        try {
          wire = await this.simulateFor(instructions, bh.blockhash, LADDER_LIMITS, true);
        } catch (e) {
          const routeFailure = isRouteFailure(e);
          if (isVenueRejection(e, toBase58(this.programId))) for (const v of plan.routeLabels) this.sessionExclusions.add(v);
          if (!plan.request.bounded || !routeFailure || attempt >= 2) throw e;
          onProgress({ step: 'checking', leaf: auth.leaf, note: 'Retrying with a new route, same signature.' });
          const fresh = await this.quoteSwap(plan.request, signer);
          const again = assembleExecute(fresh.ladder.route, fresh.expect, params);
          if (hex(headerBytes(again.header)) !== hex(headerBytes(assembly.header))) throw new Error('The fresh route needs a different authorisation; the same signature cannot cover it.');
          const resent = await signer.resend(auth.leaf, again.inners, BigInt(await this.observeSlot()));
          execute = { programId: this.programId, accounts: resent.accounts, data: resent.instructionData };
          instructions = await transactionInstructionsAround(execute, this.programId, this.vaultAddressBytes, this.relayer.publicKey, fresh.mints);
        }
      }
      onProgress({ step: 'sending', leaf: auth.leaf, signature: signatureOfWire(wire), validUntilSlot: params.validUntilSlot });
      sent = await this.sendWire(signer, auth.leaf, wire);
    } catch (e) {
      return this.outcome(signer, auth.leaf, e, onProgress, swapFailureText);
    }
    const readback = await this.settle(signer);
    const { receipt, receiptNote } = await this.receiptOf(sent.signature, plan);
    onProgress({ step: 'landed', leaf: auth.leaf, signature: sent.signature, ...(readback ? { note: readback } : {}) });
    return { signature: sent.signature, leaf: auth.leaf, transactionSlot: sent.transactionSlot, readback, receipt, receiptNote };
  }

  /** The landed swap's receipt from its own transaction (R46), or the reason it could not be read. Never throws. Public so an outcome resolved later (`reconcile`) can still get its receipt. */
  async swapReceiptFor(signature: string, plan: SwapPlan): Promise<{ receipt: SwapReceipt | null; receiptNote: string | null }> {
    return this.receiptOf(signature, plan);
  }

  private async receiptOf(signature: string, plan: SwapPlan): Promise<{ receipt: SwapReceipt | null; receiptNote: string | null }> {
    try {
      const tx = await this.rpc.getTransactionJson(signature);
      if (!tx) return { receipt: null, receiptNote: 'The receipt could not be read: the RPC endpoint does not return the transaction yet. The overview shows the balances.' };
      // The side-aware form (R46): on the input side the fee transfer is read from the input account, and the output is the whole delivery.
      // With the signed bounds (R63): figures the program could not have settled, such as nothing arriving, are refused, not shown.
      const r = swapReceipt(tx, { ...receiptAccounts(plan.expect), minOut: plan.grossMin, maxIn: plan.maxIn });
      if (r.ok) return { receipt: r.receipt, receiptNote: null };
      return { receipt: null, receiptNote: `The receipt could not be read from the transaction: ${r.reason}. The overview shows the balances.` };
    } catch (e) {
      return { receipt: null, receiptNote: `The receipt could not be read from the transaction: ${describe(e)} The overview shows the balances.` };
    }
  }

  /**
   * Local test clusters only (the rpc shim, a test validator): fund the gas
   * tank without a wallet. Never offered against a public host. Waits on the
   * balance rather than on the airdrop's signature status, which the shim
   * does not track the way it tracks sent transactions.
   */
  async airdrop(lamports: bigint): Promise<string> {
    const before = await this.rpc.getBalance(this.relayerAddress);
    const sig = await this.rpc.requestAirdrop(this.relayerAddress, lamports);
    for (let i = 0; i < 40; i++) {
      if ((await this.rpc.getBalance(this.relayerAddress)) > before) return sig;
      await new Promise((r) => setTimeout(r, 500));
    }
    throw new Error('the airdrop was requested but the balance did not change within 20 s');
  }

  // ---------------------------------------------------------------- staking
  //
  // Native delegation (docs/19-staking-plan.md 4.2 the payloads, 6.1 the flows,
  // 6.3 the checks; ST6b). Every step is one Execute and one one-time key,
  // EXACT mode, with EXECUTOR = the fee reserve (one aux account), through
  // runSigned, so the guard, the ledger, the four outcomes and the key refresh
  // are those of every other action. A failure after the key is used retires
  // it (R11), so `planStake` runs every check of docs/19 6.3 before
  // `authorize`: at review time, and again, read fresh, inside the build step
  // just before the key is reserved.

  /** The vault's wrapped-SOL account, where staking takes SOL from and returns it to. */
  async wsolAta(): Promise<Uint8Array> {
    return ataAddress(this.vaultAddressBytes, WSOL_MINT, TOKEN_PROGRAM);
  }

  /** The staking gate of docs/19 6.3 item 8 (staking.ts), read now. */
  async stakingGate(): Promise<StakingGate> {
    return stakingGate(this.rpc, toBase58(this.programId));
  }

  /** What a staking action costs the fee reserve: its network fee, the refresh reserve and the reserve's own minimum; no account rent (the stake account's rent comes from the vault's own SOL, docs/19 6.3 item 7). */
  async stakeCost(): Promise<CostEstimate> {
    return this.estimateCost(TOKEN_TOUCHING_LIMITS, []);
  }

  /** `getEpochInfo` (docs/19 6.1: when a stake starts earning), with the slot it answered at recorded on the slot clock. */
  async epochInfo(): Promise<{ epoch: bigint; slotIndex: bigint; slotsInEpoch: bigint; absoluteSlot: number }> {
    const r = await this.rpc.call<{ epoch: number; slotIndex: number; slotsInEpoch: number; absoluteSlot: number }>('getEpochInfo', [{ commitment: 'confirmed' }]);
    this.clock.observe(r.absoluteSlot);
    return { epoch: BigInt(r.epoch), slotIndex: BigInt(r.slotIndex), slotsInEpoch: BigInt(r.slotsInEpoch), absoluteSlot: r.absoluteSlot };
  }

  /**
   * The Stake program's minimum delegation, from an unsigned `GetMinimumDelegation` simulation (docs/19 6.3 item 3):
   * its return data, read from the response's `returnData` or the logs' "Program return:" line. When neither is
   * available the network's documented 1 SOL is assumed and the plan says so (`minimumSource: 'assumed'`).
   */
  private async minimumDelegation(minContextSlot: number): Promise<{ lamports: bigint; source: 'simulated' | 'assumed' }> {
    try {
      const r = await sk.dryRunInner(this.rpc, [{ program: sk.STAKE_PROGRAM, metas: [], data: u32le(13) }], this.relayer.publicKey, this.vaultAddressBytes, { minContextSlot });
      if (r.ok) {
        const line = r.logs.find((l) => l.startsWith(`Program return: ${STAKE_PROGRAM_B58} `));
        const b64 = line?.slice(`Program return: ${STAKE_PROGRAM_B58} `.length).trim();
        if (b64) {
          const bytes = Uint8Array.from(atob(b64), (c) => c.charCodeAt(0));
          if (bytes.length >= 8) return { lamports: new DataView(bytes.buffer).getBigUint64(0, true), source: 'simulated' };
        }
      }
    } catch {
      /* assumed below */
    }
    return { lamports: 1_000_000_000n, source: 'assumed' };
  }

  /**
   * Every check of docs/19 6.3 before a staking action may use a key, in its order: (1) getEpochInfo, the bank's
   * schedule and so the epoch boundary (the EpochSchedule sysvar account is read too, but only compared), then one
   * snapshot through the SDK's discovery, the sysvars, the vault, its wrapped-SOL account, the vote account and the
   * stake account acted on read in the same getMultipleAccounts call, whose Clock epoch must be getEpochInfo's;
   * (2) the reward window with a 300-slot margin, which also sets the
   * signature's expiry; (3) the amounts (Rent(200) and Rent(165) from the snapshot's Rent, the minimum delegation from
   * an unsigned simulation; withdrawals through the SDK's withdraw limits); (4) the account states; (5) the vote
   * account; (6) the structural mirror of the vault's own rules (it must say "lands") and the unsigned dry run of the
   * inner instructions; plus (8) the program gates. Throws StakeRefused, in the user's words, before any key; returns
   * everything the review shows and the build signs.
   */
  async planStake(action: StakeAction, minContextSlot?: number): Promise<StakePlan> {
    const programB58 = toBase58(this.programId);
    // (8) the programs: the vault build with the staking rules, Stake v5.1.0, p-token v1.0.0.
    const gate = await stakingGate(this.rpc, programB58);
    if (!gate.on) throw new StakeRefused(gate.reason ?? 'Staking is off on this network.');
    const ata = await this.wsolAta();
    const voteB58 = 'vote' in action ? action.vote : null;
    let voteBytes: Uint8Array | null = null;
    if (voteB58) {
      try {
        voteBytes = addressBytes(address(voteB58));
      } catch {
        throw new StakeRefused('The validator address is not a valid Solana address.');
      }
    }
    const nKnown = 'n' in action ? action.n : null;
    const target = nKnown !== null ? sk.stakeAddress(this.vaultAddressBytes, nKnown) : null;

    // (1) The epoch boundary is the bank's: getEpochInfo, read once, here, before the snapshot and the dry run (the
    // EpochSchedule sysvar account is only compared: devnet's holds a stale 8,192 slots per epoch; ST5). Then one
    // snapshot (getMultipleAccounts over the 64 stake addresses and these, then the getProgramAccounts cross-check at
    // the same slot), whose Clock epoch must be getEpochInfo's.
    const info = await this.epochInfo();
    const extra = [SYSVAR.clock, SYSVAR.epochSchedule, SYSVAR.epochRewards, SYSVAR.rent, SYSVAR.stakeHistory, this.info.address, toBase58(ata), ...(voteB58 ? [voteB58] : []), ...(target ? [toBase58(target)] : [])];
    const ctx = minContextSlot ?? (await this.observeSlot());
    let found: sk.Discovery;
    try {
      found = await sk.discoverStake(this.rpc, this.vaultAddressBytes, { extra, minContextSlot: ctx });
    } catch (e) {
      throw new StakeRefused(`the vault's stake accounts could not be read consistently (${describe(e)}). Try again in a moment.`);
    }
    const [clockAcc, scheduleAcc, rewardsAcc, rentAcc, historyAcc, vaultAcc, ataAcc] = found.extra;
    const voteAcc = voteB58 ? (found.extra[7] ?? null) : null;
    const targetAcc = target ? (found.extra[voteB58 ? 8 : 7] ?? null) : null;
    if (!clockAcc || !rewardsAcc || !rentAcc || !historyAcc) throw new StakeRefused("the network's staking clock and rent could not be read.");
    if (!vaultAcc) throw new StakeRefused('the vault account could not be read.');
    const clock = parseClock(clockAcc.data);
    const rent = parseRent(rentAcc.data);
    const epoch = clock.epoch;
    if (epoch !== info.epoch) throw new StakeRefused('the network moved to a new staking period while the app was checking. Review again; your key was not used.');
    let schedule: sk.EpochSchedule;
    try {
      schedule = sk.epochScheduleFromEpochInfo(info);
    } catch (e) {
      throw new StakeRefused(`the network's staking period could not be read (${describe(e)}). Try again in a moment.`);
    }
    // The sysvar account, for the record: a disagreement is kept in the plan and logged, never fatal; the bank's figure wins.
    const sysvarSchedule = scheduleAcc && scheduleAcc.data.length >= 33 ? sk.parseEpochSchedule(scheduleAcc.data) : null;
    const scheduleCheck = sysvarSchedule ? sk.checkEpochSchedule(sysvarSchedule, info) : { agrees: false, note: 'sysvar account unreadable' };
    const epochScheduleSource = `getEpochInfo; ${scheduleCheck.agrees ? 'sysvar account agrees' : scheduleCheck.note}`;
    if (!scheduleCheck.agrees) console.info(`[staking] epoch schedule: ${epochScheduleSource}`);
    const history = sk.StakeHistory.parse(epoch, historyAcc.data);
    const slot = BigInt(found.contextSlot);

    // (2) Not in or near the reward window; the boundary from getEpochInfo.
    const active = sk.epochRewardsActive(rewardsAcc.data);
    if (active === null) throw new StakeRefused("the network's reward state could not be read.");
    const nextEpochStart = sk.firstSlotInEpoch(epoch + 1n, schedule);
    const window = sk.rewardWindow(active, slot, nextEpochStart, REWARD_MARGIN_SLOTS);
    if (!window.ok) throw new StakeRefused(active ? 'the network is paying out staking rewards right now, and the Stake program accepts no changes until it is done. Try again in a few minutes; your key was not used.' : 'the network’s current staking period ends in less than about 80 seconds. Try again just after the next one begins; your key was not used.');
    const validUntilSlot = window.value;

    // (3) Amounts: rent from the snapshot's Rent; the minimum delegation from an unsigned simulation.
    const rent200 = rentFor(rent, 200);
    const rent165 = rentFor(rent, 165);
    if (!ataAcc) throw new StakeRefused('the vault has no swappable SOL yet. Choose Make swappable on the overview first.');
    // A native token account keeps the reserve it was created with, which may be above today's Rent(165).
    const stored = ataAcc.data.length >= 121 && ataAcc.data[109] === 1 ? new DataView(ataAcc.data.buffer, ataAcc.data.byteOffset).getBigUint64(113, true) : 0n;
    const keep165 = stored > rent165 ? stored : rent165;
    const min = await this.minimumDelegation(found.contextSlot);
    const n = action.kind === 'stake' ? found.nextFreeSeed : nKnown!;
    if (n === null) throw new StakeRefused('all 64 stake account addresses of this vault are in use.');
    const p: sk.Position = { vault: this.vaultAddressBytes, ata, n };
    const stakeAcc = sk.stakeAddress(this.vaultAddressBytes, n);
    const words = refusalWords(rent200, min.lamports, keep165);

    // (4) The account acted on.
    let state: sk.StakeState | null = null;
    let lamports = 0n;
    if (action.kind !== 'stake') {
      if (!targetAcc) throw new StakeRefused('this stake account does not exist any more.');
      const controlled = sk.checkControlled(targetAcc.owner, targetAcc.data, this.vaultAddressBytes);
      if (!controlled.ok) throw new StakeRefused(words(controlled.error));
      state = sk.decode(targetAcc.data);
      lamports = targetAcc.lamports;
    }
    const limits = state ? sk.withdrawLimits(state, lamports, rent200, epoch, history) : null;

    let inner: InnerIx[];
    let unwrap = 0n;
    let withdraw = 0n;
    let m: number | undefined;
    const check = (c: sk.Checked) => {
      if (!c.ok) throw new StakeRefused(words(c.error));
    };
    switch (action.kind) {
      case 'stake': {
        unwrap = action.lamports + rent200;
        check(sk.checkStakeAmount(unwrap, rent200, min.lamports, ataAcc.lamports, keep165));
        // A new stake account's address is the lowest seed whose slot the SDK's classify calls Free (absent, or a System
        // account with no data: checkNewSeed's own predicate), over this same snapshot (discoverStake's nextFreeSeed).
        inner = sk.stakeNew(p, voteBytes!, unwrap);
        break;
      }
      case 'again': {
        unwrap = action.lamports;
        check(sk.checkStakeAmount(lamports + unwrap, rent200, min.lamports, U64_MAX, 0n));
        check(sk.checkStakeAmount(unwrap, 0n, 0n, ataAcc.lamports, keep165));
        check(sk.checkDelegate(state!, voteBytes!, epoch, history));
        inner = sk.stakeAgain(p, voteBytes!, unwrap);
        break;
      }
      case 'unstake':
        check(sk.checkDeactivate(state!));
        inner = sk.unstake(p);
        break;
      case 'part': {
        check(sk.checkDeactivate(state!));
        const delegated = state!.kind === 'Stake' ? state!.stake.delegation.stake : 0n;
        if (action.lamports < min.lamports || delegated - action.lamports < min.lamports) throw new StakeRefused(`both parts must keep at least ${solText(min.lamports)} staked, so from this position between ${solText(min.lamports)} and ${solText(delegated > min.lamports ? delegated - min.lamports : 0n)} can be unstaked.`);
        check(sk.checkStakeAmount(rent200, 0n, 0n, ataAcc.lamports, keep165));
        m = found.nextFreeSeed ?? undefined;
        if (m === undefined || m === n) throw new StakeRefused('all 64 stake account addresses of this vault are in use.');
        unwrap = rent200;
        inner = sk.unstakePart(p, m, action.lamports, rent200);
        break;
      }
      case 'savings': {
        withdraw = lamports > rent200 ? lamports - rent200 : 0n;
        if (withdraw === 0n) throw new StakeRefused('nothing to move: the stake account holds only its rent.');
        check(sk.checkWithdrawAmount(withdraw, lamports, limits!));
        inner = sk.moveToSavings(p, lamports, rent200);
        break;
      }
      case 'close':
        withdraw = lamports;
        check(sk.checkWithdrawAmount(withdraw, lamports, limits!));
        inner = sk.closePosition(p, lamports);
        break;
      case 'switch':
        check(sk.checkDelegate(state!, voteBytes!, epoch, history));
        if (lamports < rent200 + min.lamports) throw new StakeRefused(`this stake account holds less than ${solText(min.lamports)} above its rent, the smallest stake the network accepts. Stake again into it with more instead.`);
        inner = sk.switchValidator(p, voteBytes!);
        break;
      case 'collect': {
        if (!limits || limits.staked === 0n) throw new StakeRefused('this position is not staked; move it to savings instead.');
        withdraw = action.lamports ?? limits.partialMax;
        if (withdraw === 0n) throw new StakeRefused('there is nothing above the stake and its rent to collect.');
        check(sk.checkWithdrawAmount(withdraw, lamports, limits));
        inner = sk.collectExtra(p, withdraw);
        break;
      }
    }

    // (5) The vote account: owned by the Vote program, VoteStateV4, credits in the latest epochs; commission in basis points.
    let vote: VoteCheck | null = null;
    if (voteB58) {
      vote = checkVoteAccount(voteB58, voteAcc, epoch);
      if (vote.refusal) throw new StakeRefused(lowerFirst(vote.refusal));
    }

    // (6) The vault's own rules, mirrored (the dry run cannot run them), then the unsigned dry run.
    const ruleAccounts: sk.RuleAccount[] = [];
    const add = (addr: Uint8Array, a: OnChainAccount | null | undefined) => {
      if (a) ruleAccounts.push({ address: addr, owner: a.owner, lamports: a.lamports, data: a.data });
    };
    add(ata, ataAcc);
    add(this.vaultAddressBytes, vaultAcc);
    if (voteBytes) add(voteBytes, voteAcc);
    if (target) add(target, targetAcc);
    const header: sk.RuleHeader = { flags: FLAGS.EXECUTOR, executor: this.relayer.publicKey, aux: [{ address: this.relayer.publicKey, isWritable: false, isSigner: true }] };
    const rules = sk.stakeRuleCheck(header, inner, ruleAccounts, { vault: this.vaultAddressBytes });
    if (rules.outcome !== 'lands') {
      const code = typeof rules.outcome === 'object' && 'vaultError' in rules.outcome ? `code ${rules.outcome.vaultError}` : String(Object.values(rules.outcome)[0]);
      throw new StakeRefused(`the vault program's staking rules would refuse this transaction (${code}), so it is not signed.`);
    }
    const dry = await sk.dryRunInner(this.rpc, inner, this.relayer.publicKey, this.vaultAddressBytes, { minContextSlot: found.contextSlot, limits: TOKEN_TOUCHING_LIMITS });
    const dryCheck = sk.checkDryRun(dry, slot, schedule);
    if (!dryCheck.ok) {
      if (dryCheck.error.kind === 'EpochChanged') throw new StakeRefused('the network moved to a new staking period while the app was checking. Review again; your key was not used.');
      throw new StakeRefused(`a dry run without any key failed: ${stakeFailureText(dry.logs, dry.err, programB58)} Your key was not used.`);
    }

    // When it starts earning (docs/19 6.1): the slots left before getEpochInfo's boundary, from the snapshot's slot.
    return {
      action,
      n,
      m,
      stakeAddress: toBase58(stakeAcc),
      inner,
      unwrap,
      withdraw,
      lamportsBefore: lamports,
      rent200,
      rent165: keep165,
      minDelegation: min.lamports,
      minimumSource: min.source,
      epoch,
      slot,
      validUntilSlot,
      slotsLeftInEpoch: nextEpochStart > slot ? nextEpochStart - slot : 0n,
      vote,
      dryRunUnits: dry.unitsConsumed ?? null,
      contextSlot: found.contextSlot,
      epochScheduleSource,
    };
  }

  /** The one signed path of every staking action: runSigned with the checks of planStake inside its build step (before authorize), the CU limit tightened to 1.2 x the signed simulation, and a failure named by the program that failed (docs/19 4.4.6). */
  private async runStaking(signer: VaultSigner, action: StakeAction, onProgress: (p: ActionProgress) => void): Promise<StakeResult> {
    const held: { plan: StakePlan | null } = { plan: null };
    const programB58 = toBase58(this.programId);
    const relay = (p: ActionProgress) => onProgress(p.step === 'signing' && !held.plan ? { ...p, note: 'Checking the stake account, the validator and a dry run without any key. No key is used until these pass.' } : p);
    try {
      const r = await this.runSigned(
        signer,
        async (slot) => {
          held.plan = await this.planStake(action, Number(slot));
          onProgress({ step: 'signing' });
          return {
            header: { numAux: 1, validUntilSlot: held.plan.validUntilSlot, executor: this.relayer.publicKey },
            aux: [{ pubkey: this.relayer.publicKey, isSigner: true, isWritable: false }],
            inners: held.plan.inner,
            top: [],
            limits: TOKEN_TOUCHING_LIMITS,
            purpose: 'other',
            newAccounts: [],
          };
        },
        relay,
        { tighten: true, text: (cause) => stakeCauseText(cause, programB58) },
      );
      const plan = held.plan as StakePlan | null;
      if (plan && plan.vote?.info && (action.kind === 'stake' || action.kind === 'again' || action.kind === 'switch')) noteCommission(plan.stakeAddress, plan.vote.vote, plan.vote.info.commissionBps);
      return { ...r, plan: plan! };
    } catch (e) {
      // Landed and failed: the confirmation carries only the error, so the logs are read back to name the program.
      if (e instanceof ActionFailed && e.kind === 'failed' && e.signature) {
        const logs = await this.rpc
          .call<{ meta?: { logMessages?: string[]; err?: unknown } } | null>('getTransaction', [e.signature, { encoding: 'json', maxSupportedTransactionVersion: 1, commitment: 'confirmed' }])
          .then((t) => t?.meta?.logMessages ?? [])
          .catch(() => [] as string[]);
        if (hasFailingProgram(logs)) {
          const detail = stakeFailureText(logs, (e.cause as { detail?: unknown } | undefined)?.detail ?? null, programB58);
          onProgress({ step: 'failed', signature: e.signature, error: `${FAILED_SENTENCE} ${detail}` });
          throw new ActionFailed('failed', detail, e.cause, e.signature);
        }
      }
      throw e;
    }
  }

  /** Stake `lamports` (the amount to delegate; the stake account's rent comes on top, from the vault's own SOL) into a new stake account, with the validator `vote` (docs/19 4.2 row 1). */
  async stake(signer: VaultSigner, vote: string, lamports: bigint, onProgress: (p: ActionProgress) => void): Promise<StakeResult> {
    return this.runStaking(signer, { kind: 'stake', vote, lamports }, onProgress);
  }

  /** Stake `lamports` more into the existing, inactive stake account `n` (its rent is already there), with `vote` (row 2). */
  async stakeAgain(signer: VaultSigner, n: number, vote: string, lamports: bigint, onProgress: (p: ActionProgress) => void): Promise<StakeResult> {
    return this.runStaking(signer, { kind: 'again', n, vote, lamports }, onProgress);
  }

  /** Unstake the whole position `n` (row 3). */
  async unstake(signer: VaultSigner, n: number, onProgress: (p: ActionProgress) => void): Promise<StakeResult> {
    return this.runStaking(signer, { kind: 'unstake', n }, onProgress);
  }

  /** Unstake `lamports` of position `n` into a new stake account of its own (row 4). */
  async unstakePart(signer: VaultSigner, n: number, lamports: bigint, onProgress: (p: ActionProgress) => void): Promise<StakeResult> {
    return this.runStaking(signer, { kind: 'part', n, lamports }, onProgress);
  }

  /** Move a withdrawable position's SOL back to the vault's savings, keeping the stake account open with its rent (row 6, the default). */
  async moveToSavings(signer: VaultSigner, n: number, onProgress: (p: ActionProgress) => void): Promise<StakeResult> {
    return this.runStaking(signer, { kind: 'savings', n }, onProgress);
  }

  /** Close the stake account: everything, rent included, back to savings (row 7, explicit). */
  async closeStake(signer: VaultSigner, n: number, onProgress: (p: ActionProgress) => void): Promise<StakeResult> {
    return this.runStaking(signer, { kind: 'close', n }, onProgress);
  }

  /** Delegate an unstaked position `n` to another validator, with no withdrawal (row 8). */
  async switchValidator(signer: VaultSigner, n: number, vote: string, onProgress: (p: ActionProgress) => void): Promise<StakeResult> {
    return this.runStaking(signer, { kind: 'switch', n, vote }, onProgress);
  }

  /** Move what a staked position holds above its stake and rent back to savings (row 9); all of it when `lamports` is omitted. */
  async collectExtra(signer: VaultSigner, n: number, lamports: bigint | undefined, onProgress: (p: ActionProgress) => void): Promise<StakeResult> {
    return this.runStaking(signer, { kind: 'collect', n, ...(lamports !== undefined ? { lamports } : {}) }, onProgress);
  }

  /**
   * The vault's stake accounts as the overview lists them (docs/19 6.2, 4.8): the SDK's discovery with Clock, Rent and
   * StakeHistory in the same read; each owned position with its status. Reading only; the gates are read only when
   * there is something to act on, so a vault with no stake costs two reads.
   */
  async stakePositions(): Promise<StakeHoldings> {
    const found = await sk.discoverStake(this.rpc, this.vaultAddressBytes, { extra: [SYSVAR.clock, SYSVAR.rent, SYSVAR.stakeHistory] });
    const [clockAcc, rentAcc, historyAcc] = found.extra;
    if (!clockAcc || !rentAcc || !historyAcc) throw new Error("the network's staking clock could not be read");
    const epoch = parseClock(clockAcc.data).epoch;
    const history = sk.StakeHistory.parse(epoch, historyAcc.data);
    const rent200 = rentFor(parseRent(rentAcc.data), 200);
    const owned = found.owned.map((p) => viewPosition(p, epoch, history, rent200));
    const named = found.named.map((p) => viewPosition(p, epoch, history, rent200));
    const gate = owned.length ? await this.stakingGate() : null;
    return { epoch, rent200, owned, named, gate, nextFreeSeed: found.nextFreeSeed };
  }

  async close(): Promise<void> {
    this.closed = true;
    // the held activation attempt goes with the engine (docs/18 5.1 D step 6): after a lock the device stays recovery-only
    this.activation = null;
    if (this.signer) await this.signer.close();
    this.signer = null;
    this.store.close();
  }
}

// ------------------------------------------------------------------ staking

/** docs/19 6.3 item 2: no staking signature is made within this many slots of the epoch's end (about 80 s at 0.27 s per slot, twice the 150-slot validity). */
export const REWARD_MARGIN_SLOTS = 300n;

/** A staking action, as the Stake page and the overview's rows ask for it. `lamports` of `stake` is the amount delegated; the stake account's rent comes on top. */
export type StakeAction =
  | { kind: 'stake'; vote: string; lamports: bigint }
  | { kind: 'again'; n: number; vote: string; lamports: bigint }
  | { kind: 'unstake'; n: number }
  | { kind: 'part'; n: number; lamports: bigint }
  | { kind: 'savings'; n: number }
  | { kind: 'close'; n: number }
  | { kind: 'switch'; n: number; vote: string }
  | { kind: 'collect'; n: number; lamports?: bigint };

/** Everything the staking review shows and the build signs, from one pass of planStake's checks (docs/19 6.3). */
export interface StakePlan {
  action: StakeAction;
  /** The stake account acted on (for a new stake, the one it creates). */
  n: number;
  /** Unstake part: the new stake account the split part goes to. */
  m?: number;
  stakeAddress: string;
  inner: InnerIx[];
  /** Lamports leaving the vault's savings (its wrapped SOL) into the stake account; 0 when none. */
  unwrap: bigint;
  /** Lamports coming back to the vault's savings; 0 when none. */
  withdraw: bigint;
  /** The stake account's lamports before (0 for a new one). */
  lamportsBefore: bigint;
  /** Rent(200), the stake account's rent, from the snapshot's Rent sysvar. */
  rent200: bigint;
  /** What the vault's wrapped-SOL account must keep: Rent(165), or its stored reserve when that is higher. */
  rent165: bigint;
  minDelegation: bigint;
  /** 'simulated': read from the Stake program by an unsigned GetMinimumDelegation; 'assumed': 1 SOL, because that simulation gave no answer. */
  minimumSource: 'simulated' | 'assumed';
  epoch: bigint;
  slot: bigint;
  validUntilSlot: bigint;
  slotsLeftInEpoch: bigint;
  vote: VoteCheck | null;
  dryRunUnits: number | null;
  contextSlot: number;
  /** Where the epoch boundary came from: "getEpochInfo; sysvar account agrees", or the EpochSchedule sysvar account's disagreement (sk.checkEpochSchedule's note). Logged, never shown. */
  epochScheduleSource: string;
}

export interface StakeResult extends ActionResult {
  plan: StakePlan;
}

/** The overview's Staked group (docs/19 6.2). `gate` is read only when an owned position exists. */
export interface StakeHoldings {
  epoch: bigint;
  rent200: bigint;
  owned: PositionView[];
  named: PositionView[];
  gate: StakingGate | null;
  nextFreeSeed: number | null;
}

/** A staking action refused by a check before any key was used (docs/19 6.3): nothing was signed, nothing retired. */
export class StakeRefused extends Error {
  constructor(readonly reason: string) {
    super(`Nothing was signed: ${reason}`);
    this.name = 'StakeRefused';
  }
}

function lowerFirst(s: string): string {
  return s ? s[0]!.toLowerCase() + s.slice(1) : s;
}

/** The SDK's precheck refusals in the user's words (docs/08 section 9). */
function refusalWords(rent200: bigint, minDelegation: bigint, keep165: bigint): (p: sk.Precheck) => string {
  return (p) => {
    switch (p.kind) {
      case 'BelowMinimum':
        return `the smallest stake the network accepts is ${solText(minDelegation)}, and the stake account keeps ${solText(rent200)} of rent on top (${solText(p.need)} in all).`;
      case 'ExceedsSavings':
        return `the vault's swappable SOL has ${solText(p.available)} available for staking (its account keeps ${solText(keep165)}). Make more SOL swappable first, or stake less.`;
      case 'WithdrawTooLarge':
        return p.limit > 0n ? `only ${solText(p.limit)} can be moved now; the rest is still staked or is the account's rent.` : 'nothing can be moved now: this position is still staked. Unstake it, and move it once it is ready.';
      case 'SeedOccupied':
        return 'the next stake account address is already in use.';
      case 'NotControlled':
        return 'this stake account is not controlled by your vault alone, so the app offers no action on it.';
      case 'StillActive':
        return `this position still has ${solText(p.effective)} staked. Unstake it and wait until it is ready before choosing a validator.`;
      case 'AlreadyDeactivated':
        return 'this position is already unstaking.';
      case 'RewardWindow':
        return 'the network is paying out staking rewards, or the current staking period is about to end. Try again in a few minutes.';
      case 'DryRunFailed':
        return 'a dry run without any key failed.';
      case 'EpochChanged':
        return 'the network moved to a new staking period while the app was checking.';
    }
  };
}

/** Readable names of the programs a staking transaction runs (docs/19 4.4.6: a failure is named by the failing program, never by its number alone, since Stake and vault error numbers collide). */
function programName(id: string, vaultProgram: string): string {
  if (id === STAKE_PROGRAM_B58) return 'the Stake program';
  if (id === 'TokenkegQfeZyiNwAJbNbGKPFXCWuBvf9Ss623VQ5DA') return 'the Token program';
  if (id === '11111111111111111111111111111111') return 'the System program';
  if (id === vaultProgram) return 'the vault program';
  return `the program ${id.slice(0, 4)}…${id.slice(-4)}`;
}

/** The runtime's own failure messages that a staking action can meet, in the user's words; any other message, and every program error number, is left out of the sentence (the program's name says who refused). */
const PLAIN_REASONS: Array<[RegExp, string]> = [[/insufficient funds/i, "an account's balance did not allow it"]];

/** "The Stake program refused it." from a transaction's logs: the first "Program X failed: ..." line is the innermost failing program, named in words and never by an error number (docs/19 4.4.6, docs/08 section 9). Falls back to "The network refused it." when the logs name none. */
export function stakeFailureText(logs: string[], _err: unknown, vaultProgramB58: string): string {
  const line = logs.find((l) => /^Program \S+ failed: /.test(l));
  const m = line ? /^Program (\S+) failed: (.*)$/.exec(line) : null;
  if (m) {
    const program = programName(m[1]!, vaultProgramB58).replace(/^the/, 'The');
    const why = PLAIN_REASONS.find(([re]) => re.test(m[2]!))?.[1];
    return why ? `${program} refused it: ${why}.` : `${program} refused it.`;
  }
  return 'The network refused it.';
}

/** runSigned's outcome text for staking: a simulated refusal is named from its logs; anything else as describe() says it. */
function stakeCauseText(cause: unknown, vaultProgramB58: string): string {
  // A program's refusal is named from the logs (docs/19 4.4.6). A transaction-level error (the fee reserve's balance, an
  // account's rent) has no failing program and is worded by describe(), which names the account (docs/18 5.1 B, R78).
  if (cause instanceof RelayerError && cause.code === 'SimulationFailed') {
    const d = cause.detail as { err?: unknown; logs?: string[] } | undefined;
    if (hasFailingProgram(d?.logs ?? [])) return stakeFailureText(d?.logs ?? [], d?.err ?? null, vaultProgramB58);
  }
  // A confirmed program failure is renamed by runStaking from the transaction's logs; anything else as describe() says it.
  return describe(cause);
}

/** Whether a transaction's logs name a failing program (a "Program X failed:" line). */
function hasFailingProgram(logs: string[]): boolean {
  return logs.some((l) => /^Program \S+ failed: /.test(l));
}

export function isLocalRpc(url: string): boolean {
  try {
    const host = new URL(url).hostname;
    return host === 'localhost' || host === '127.0.0.1' || host === '[::1]';
  } catch {
    return false;
  }
}

/** Why a swap attempt failed, in the user's words; the outcome sentence in front of it says whether that was in the pre-flight check or on chain. */
export function swapFailureText(e: unknown): string {
  if (isSettleRefusal(e)) return 'The route delivered less than the minimum you saw, so the vault refused to settle.';
  if (isRouteFailure(e)) {
    const program = failingProgramOf(e);
    if (program === JUPITER_B58 || program === null) return 'The route could not deliver what the router promised.';
    return 'The venue rejected the trade. It is excluded for the rest of this session.';
  }
  return describe(e);
}

/** One sentence for a failure, in the user's words where the SDK names a reason; never the SDK's own vocabulary (docs/08 section 9). */
export function describe(e: unknown): string {
  // A sweep stopped before the send is described by what stopped it (docs/18 section 6).
  if (e instanceof SweepNotSent) return describe(e.cause);
  if (e instanceof RelayerError) {
    if (e.code === 'SimulationFailed' || e.code === 'ConfirmFailed') {
      // An error of the transaction itself names who could not pay, with the raw error and the account index (5.1 B).
      const t = transactionError(e);
      if (t?.kind === 'transaction') return transactionErrorText(t, accountKeysOf.get(e));
    }
    if (e.code === 'SimulationFailed') return 'The network refused this transaction in its pre-flight check.';
    if (e.code === 'ConfirmTimeout') return 'The network did not confirm this transaction in time. Its outcome is unknown.';
    if (e.code === 'ConfirmFailed') return 'The transaction landed but failed on chain.';
    if (e.code === 'RpcError' && /403|forbidden/i.test(e.message)) return 'This RPC endpoint refuses requests from a browser. Choose another one in Settings.';
    if (e.code === 'RpcError') return `The RPC endpoint answered with an error: ${e.message}`;
  }
  if (e instanceof SignerError) return signerText(e);
  if (e instanceof Error) return e.message;
  return String(e);
}

function signerText(e: SignerError): string {
  switch (e.code) {
    case 'RecoveryRequired':
      return 'The vault refreshes its key before this action can run.';
    case 'Unresolved':
      return "An earlier attempt's outcome is not known yet. Nothing new is signed until the keys page has checked it.";
    case 'StaleSlot':
      return 'The RPC endpoint answered with an older slot than it did before. Try again in a moment.';
    case 'NoLeaf':
    case 'LeafConflict':
      return 'This key tree has no unused key this device can use, so the vault refreshes its key first.';
    case 'KeyMismatch':
      return "The chain's key is not this phrase's.";
    case 'Expired':
      return 'That signature expired before it landed.';
    case 'Closed':
      return 'The session is locked.';
    default:
      return e.message;
  }
}

export { MARKER_LIFETIME_SLOTS };
